import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Trash2,
  Plus,
  Upload,
  X,
  FileText,
  Pencil,
  ChevronUp,
  ArrowUp,
  ArrowDown,
  Copy,
  Play,
  Check,
  MapPin,
  LocateFixed,
  Save,
  Search,
  RotateCcw,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import {
  DECIMAL_INPUT_RE,
  INT_INPUT_RE,
  parseDecimal,
  parseIntOrNull,
  sanitizeDecimal,
  sanitizeInt,
} from "@/lib/numeric-input";
import { useUpload } from "@/lib/upload";
import { fileUrl, formatPriceHuf } from "@/lib/format";
import { SplitEditor, LivePreviewPane } from "@/components/admin/split-editor";
import type {
  Project,
  ProjectUnit,
  GalleryImage,
  Floorplan,
  StylePreview,
  NeighborhoodPoi,
  PoiCategory,
  TechnicalPdf,
  TechnicalSpec,
  NewProject,
  NewProjectUnit,
  PriceItem,
  PriceItemKind,
  PricingDefaults,
} from "@shared/schema";
import {
  computeUnitPrice,
  computeLineAmounts,
  defaultPriceItem,
  boundPriceItem,
  boundAreaSqm,
  boundToM2Label,
  isSqmKind,
  priceItemKindLabel,
} from "@shared/pricing";
import type { MaintenanceSettings } from "@shared/maintenance";
import { MaintenanceFields } from "@/components/admin/maintenance-fields";
import type { BoundToM2 } from "@shared/pricing";

const NeighborhoodMapPicker = lazy(
  () => import("@/components/neighborhood-map-picker"),
);

const FloorsManager = lazy(() => import("@/components/floors-manager"));

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogv)$/i;
function isVideoMedia(item: GalleryImage): boolean {
  if (item.mimeType?.startsWith("video/")) return true;
  return VIDEO_EXT.test(item.key);
}

type Detail = { project: Project; units: ProjectUnit[] };

const STATUSES = [
  "planning",
  "construction",
  "sales",
  "completed",
] as const;

export function AdminProjectEdit({
  projectId,
}: {
  projectId: string | null;
}) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const isNew = !projectId;

  // Live-preview state: which detail-page section to scroll to + a token that
  // forces the iframe to reload after a save.
  const [previewSection, setPreviewSection] = useState<string | null>("basics");
  const [reloadToken, setReloadToken] = useState(0);
  const lastUpdatedRef = useRef<number | null>(null);

  const detailQ = useQuery({
    queryKey: ["admin", "project", projectId],
    queryFn: () =>
      api<Detail>(`/api/admin/projects/${projectId}`),
    enabled: !isNew,
  });

  // Any successful save (basics, media, units, floors) invalidates this query;
  // when it refetches, refresh the preview iframe so the live site is in sync.
  useEffect(() => {
    const u = detailQ.dataUpdatedAt;
    if (!u) return;
    if (lastUpdatedRef.current === null) {
      lastUpdatedRef.current = u;
      return;
    }
    if (u !== lastUpdatedRef.current) {
      lastUpdatedRef.current = u;
      setReloadToken((tok) => tok + 1);
    }
  }, [detailQ.dataUpdatedAt]);

  const [form, setForm] = useState<Partial<NewProject>>({
    name: "",
    slug: "",
    shortDescription: "",
    description: "",
    locationAddress: "",
    locationLat: null,
    locationLng: null,
    neighborhoodPois: [],
    neighborhoodDescription: "",
    mapEmbedUrl: "",
    mapPois: [],
    status: "planning",
    expectedCompletion: "",
    isPublished: true,
    sortOrder: 0,
    coverImageKey: null,
    gallery: [],
    floorplans: [],
    technicalPdf: null,
    technicalSpecs: [],
    pricingDefaults: null,
    maintenance: null,
  });

  // Sync form state when project loads
  useEffect(() => {
    if (detailQ.data?.project) {
      const p = detailQ.data.project;
      setForm({
        name: p.name,
        slug: p.slug,
        shortDescription: p.shortDescription ?? "",
        description: p.description ?? "",
        locationAddress: p.locationAddress ?? "",
        locationLat: p.locationLat,
        locationLng: p.locationLng,
        neighborhoodPois: p.neighborhoodPois,
        neighborhoodDescription: p.neighborhoodDescription ?? "",
        mapEmbedUrl: p.mapEmbedUrl ?? "",
        mapPois: p.mapPois,
        status: p.status,
        expectedCompletion: p.expectedCompletion ?? "",
        isPublished: p.isPublished,
        sortOrder: p.sortOrder,
        coverImageKey: p.coverImageKey,
        gallery: p.gallery,
        floorplans: p.floorplans,
        technicalPdf: p.technicalPdf,
        technicalSpecs: p.technicalSpecs,
        pricingDefaults: p.pricingDefaults,
        maintenance: p.maintenance,
      });
    }
  }, [detailQ.data]);

  const create = useMutation({
    mutationFn: () =>
      api<Project>("/api/admin/projects", {
        method: "POST",
        body: form,
      }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["admin", "projects"] });
      navigate(`/projects/${p.id}`);
    },
  });

  // `override` carries the freshly-changed field(s). React's setForm (via
  // field()) is async, so the form state captured in this closure is still
  // STALE when an upload handler fires update.mutate() right after field().
  // Merging the override on top guarantees the PATCH persists the new value
  // instead of silently re-saving the old one (the reload-and-it's-gone bug).
  const update = useMutation({
    mutationFn: (override?: Partial<NewProject>) =>
      api<Project>(`/api/admin/projects/${projectId}`, {
        method: "PATCH",
        body: { ...form, ...override },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "projects"] });
      qc.invalidateQueries({ queryKey: ["admin", "project", projectId] });
    },
  });

  const saving = create.isPending || update.isPending;
  const lastError = create.error ?? update.error;

  // Unit quick-edit rows register a "flush my draft if dirty" callback here so
  // the big "Mentés" button also saves them — testers lost row drafts because
  // the top save only PATCHed the project form (F-MANUAL-EL0NWR72).
  const unitSaversRef = useRef<Map<string, () => Promise<void>>>(new Map());
  const [dirtyUnitIds, setDirtyUnitIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const registerUnitSaver = useCallback(
    (id: string, saveIfDirty: () => Promise<void>) => {
      unitSaversRef.current.set(id, saveIfDirty);
      return () => {
        unitSaversRef.current.delete(id);
      };
    },
    [],
  );
  const reportUnitDirty = useCallback((id: string, dirty: boolean) => {
    setDirtyUnitIds((prev) => {
      if (prev.has(id) === dirty) return prev;
      const next = new Set(prev);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const unitsDirty = dirtyUnitIds.size > 0;

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (isNew) {
      create.mutate();
      return;
    }
    // Flush every dirty unit row alongside the project PATCH. allSettled: one
    // failing row doesn't block the others; each failure stays visible in the
    // row's own error UI (the callbacks reuse the rows' save mutations).
    const rowFlushes = [...unitSaversRef.current.values()].map((flush) =>
      flush(),
    );
    if (basicsDirty) update.mutate(undefined);
    void Promise.allSettled(rowFlushes);
  }

  function field<K extends keyof NewProject>(
    key: K,
    value: NewProject[K] | undefined,
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  if (!isNew && detailQ.isLoading) {
    return <div className="text-brand-500">{t("common.loading")}</div>;
  }

  const project = detailQ.data?.project;
  const basicsSnapshot = (src: Partial<NewProject>) =>
    JSON.stringify({
      name: src.name ?? "",
      slug: src.slug ?? "",
      shortDescription: src.shortDescription ?? "",
      description: src.description ?? "",
      locationAddress: src.locationAddress ?? "",
      expectedCompletion: src.expectedCompletion ?? "",
      status: src.status ?? "planning",
      sortOrder: src.sortOrder ?? 0,
      isPublished: src.isPublished ?? true,
    });
  // Draft logic for the core fields: dirty until the basics differ from the
  // last loaded project (a brand-new project is always savable).
  const basicsDirty = isNew
    ? true
    : !!project && basicsSnapshot(form) !== basicsSnapshot(project);

  const savedSlug = project?.slug ?? null;
  const previewPath = savedSlug ? `/projektek/${savedSlug}` : "/projektek";
  // The public detail route only serves published projects, so the live
  // preview becomes available once the project exists and is published.
  const canPreview = !isNew && !!savedSlug && !!project?.isPublished;

  const toolbar = (
    <>
      {lastError && (
        <span className="hidden sm:inline text-xs text-red-600 max-w-[180px] truncate">
          {lastError instanceof ApiError ? lastError.message : "Mentési hiba"}
        </span>
      )}
      {!basicsDirty && update.isSuccess && (
        <span className="hidden sm:inline-flex items-center gap-1 text-xs text-emerald-700">
          <Check size={14} /> Mentve
        </span>
      )}
      {basicsDirty && !isNew && (
        <span className="hidden sm:inline text-xs text-amber-600">
          Nem mentett alapadatok
        </span>
      )}
      {!basicsDirty && unitsDirty && (
        <span className="hidden sm:inline text-xs text-amber-600">
          Nem mentett lakás-módosítások
        </span>
      )}
      <button
        type="submit"
        form="project-basics-form"
        disabled={saving || (!basicsDirty && !unitsDirty && !isNew)}
        className="btn-primary !py-2.5 !px-5 text-xs"
      >
        <Save size={15} />
        {saving ? "Mentés..." : isNew ? "Létrehozás" : "Mentés"}
      </button>
    </>
  );

  const preview = (
    <LivePreviewPane
      path={previewPath}
      section={previewSection}
      reloadToken={reloadToken}
      placeholder={
        canPreview ? undefined : (
          <div className="max-w-xs space-y-2">
            <p className="text-sm font-medium text-brand-700">Élő előnézet</p>
            <p className="text-xs text-brand-500">
              {isNew
                ? "Mentsd el a projektet, és itt megjelenik az élő előnézet."
                : "A projekt nincs publikálva. Pipáld be a „Publikált” jelölőt és mentsd el az élő előnézethez."}
            </p>
          </div>
        )
      }
    />
  );

  return (
    <SplitEditor
      title={isNew ? t("admin.project.newTitle") : form.name || "Projekt"}
      subtitle={!isNew && form.slug ? `/projektek/${form.slug}` : undefined}
      toolbar={toolbar}
      preview={preview}
    >
      <div className="space-y-8 pb-10">
        <Zone onActivate={() => setPreviewSection("basics")}>
          <form
            id="project-basics-form"
            onSubmit={handleSave}
            className="space-y-6 card p-6"
          >
            <h2 className="font-semibold text-lg">
              {t("admin.project.basics")}
            </h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label">{t("admin.project.name")} *</label>
            <input
              required
              className="input"
              value={form.name ?? ""}
              onChange={(e) => field("name", e.target.value)}
            />
          </div>
          <div>
            <label className="label">{t("admin.project.slug")} *</label>
            <input
              required
              className="input font-mono"
              pattern="[a-z0-9-]+"
              value={form.slug ?? ""}
              onChange={(e) => field("slug", e.target.value)}
            />
            <p className="mt-1 text-xs text-brand-500">
              {t("admin.project.slugHelp")}
            </p>
          </div>
        </div>

        <div>
          <label className="label">{t("admin.project.shortDescription")}</label>
          <input
            className="input"
            value={form.shortDescription ?? ""}
            onChange={(e) => field("shortDescription", e.target.value)}
          />
        </div>

        <div>
          <label className="label">{t("admin.project.description")}</label>
          <textarea
            rows={6}
            className="input"
            value={form.description ?? ""}
            onChange={(e) => field("description", e.target.value)}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label">{t("admin.project.address")}</label>
            <input
              className="input"
              value={form.locationAddress ?? ""}
              onChange={(e) => field("locationAddress", e.target.value)}
            />
          </div>
          <div>
            <label className="label">
              {t("admin.project.expectedCompletion")}
            </label>
            <input
              className="input"
              placeholder="pl. 2026 Q3"
              value={form.expectedCompletion ?? ""}
              onChange={(e) => field("expectedCompletion", e.target.value)}
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="label">{t("admin.project.status")}</label>
            <select
              className="input"
              value={form.status ?? "planning"}
              onChange={(e) =>
                field("status", e.target.value as NewProject["status"])
              }
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`projects.statusLabel.${s}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">{t("admin.project.sortOrder")}</label>
            <input
              type="number"
              className="input"
              value={form.sortOrder ?? 0}
              onChange={(e) => field("sortOrder", Number(e.target.value))}
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm pb-2">
              <input
                type="checkbox"
                checked={form.isPublished ?? true}
                onChange={(e) => field("isPublished", e.target.checked)}
              />
              {t("admin.project.isPublished")}
            </label>
          </div>
        </div>

        {lastError && (
          <div className="text-sm text-red-600">
            {lastError instanceof ApiError
              ? `${lastError.status}: ${lastError.message}`
              : "Mentési hiba"}
          </div>
        )}

          </form>
        </Zone>

        {!isNew && projectId && (
          <>
            <Zone onActivate={() => setPreviewSection("gallery")}>
              <CoverImageSection
                currentKey={form.coverImageKey ?? null}
                onChange={(key) => {
                  field("coverImageKey", key);
                  return update.mutateAsync({ coverImageKey: key });
                }}
              />
            </Zone>
            <Zone onActivate={() => setPreviewSection("gallery")}>
              <GallerySection
                gallery={form.gallery ?? []}
                onChange={(gallery) => {
                  field("gallery", gallery);
                  return update.mutateAsync({ gallery });
                }}
              />
            </Zone>
            <Zone onActivate={() => setPreviewSection("floorplans")}>
              <FloorplansSection
                floorplans={form.floorplans ?? []}
                onChange={(floorplans) => {
                  field("floorplans", floorplans);
                  return update.mutateAsync({ floorplans });
                }}
              />
            </Zone>
            <Zone onActivate={() => setPreviewSection("neighborhood")}>
              <NeighborhoodSection
                address={form.locationAddress ?? ""}
                lat={form.locationLat ?? null}
                lng={form.locationLng ?? null}
                pois={form.neighborhoodPois ?? []}
                description={form.neighborhoodDescription ?? ""}
                onSave={(partial) => {
                  setForm((f) => ({ ...f, ...partial }));
                  return update.mutateAsync(partial);
                }}
              />
            </Zone>
            <Zone onActivate={() => setPreviewSection("technical")}>
              <TechnicalContentSection
                pdf={form.technicalPdf ?? null}
                specs={form.technicalSpecs ?? []}
                onSavePdf={(technicalPdf) => {
                  field("technicalPdf", technicalPdf);
                  return update.mutateAsync({ technicalPdf });
                }}
                onSaveSpecs={(technicalSpecs) => {
                  field("technicalSpecs", technicalSpecs);
                  return update.mutateAsync({ technicalSpecs });
                }}
              />
            </Zone>
            <Zone onActivate={() => setPreviewSection("units")}>
              <ProjectMaintenanceSection
                value={form.maintenance ?? null}
                onSave={(maintenance) => {
                  field("maintenance", maintenance);
                  return update.mutateAsync({ maintenance });
                }}
              />

              <PricingDefaultsSection
                value={form.pricingDefaults ?? null}
                onSave={(pricingDefaults) => {
                  field("pricingDefaults", pricingDefaults);
                  return update.mutateAsync({ pricingDefaults });
                }}
              />
            </Zone>
            <Zone onActivate={() => setPreviewSection("units")}>
              <UnitsSection
                projectId={projectId}
                units={detailQ.data?.units ?? []}
                registerSaver={registerUnitSaver}
                reportDirty={reportUnitDirty}
                pricingDefaults={form.pricingDefaults ?? null}
              />
            </Zone>
            <Zone onActivate={() => setPreviewSection("units")}>
              <Suspense
                fallback={
                  <section className="card p-6 text-sm text-brand-500">
                    Emeletek betöltése…
                  </section>
                }
              >
                <FloorsManager
                  projectId={projectId}
                  units={detailQ.data?.units ?? []}
                />
              </Suspense>
            </Zone>
          </>
        )}
      </div>
    </SplitEditor>
  );
}

/**
 * Wraps an editor section so that focusing or clicking inside it points the
 * live preview at the matching project-detail section.
 */
function Zone({
  onActivate,
  children,
}: {
  onActivate: () => void;
  children: React.ReactNode;
}) {
  return (
    <div onFocusCapture={onActivate} onMouseDownCapture={onActivate}>
      {children}
    </div>
  );
}

// ─── Cover image ───────────────────────────────────────────────────────────

function CoverImageSection({
  currentKey,
  onChange,
}: {
  currentKey: string | null;
  /** Persists the change (PATCH); resolves on success, rejects on failure. */
  onChange: (key: string | null) => void | Promise<unknown>;
}) {
  const { uploading, uploadError, uploadSuccess, run } = useUpload();
  const inputRef = useRef<HTMLInputElement>(null);

  function handleUpload(file: File) {
    run(async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "projects/covers");
      const res = await api<{ key: string }>("/api/admin/upload", {
        method: "POST",
        formData: fd,
      });
      // Await the PATCH so success/error reflects whether the key actually
      // persisted to the project record — not just the R2 upload.
      await onChange(res.key);
    });
  }

  return (
    <section className="card p-6">
      <h2 className="font-semibold text-lg">Borítókép</h2>
      <div className="mt-4">
        {currentKey ? (
          <div className="relative inline-block">
            <img
              src={fileUrl(currentKey)}
              alt=""
              className="max-w-md max-h-64 rounded-lg border border-brand-200"
            />
            <button
              type="button"
              onClick={() => run(async () => void (await onChange(null)))}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white grid place-items-center hover:bg-red-600"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <p className="text-sm text-brand-500 mb-3">Nincs borítókép.</p>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleUpload(f);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="btn-secondary mt-3"
      >
        <Upload size={16} />
        {uploading ? "Feltöltés..." : "Borítókép feltöltése"}
      </button>
      {uploadError && (
        <p className="mt-2 text-sm text-red-600">{uploadError}</p>
      )}
      {uploadSuccess && (
        <p className="mt-2 text-sm text-green-600">Feltöltve ✓</p>
      )}
    </section>
  );
}

// ─── Gallery ───────────────────────────────────────────────────────────────

function GallerySection({
  gallery,
  onChange,
  title = "Galéria",
  prefix = "projects/gallery",
  flat = false,
}: {
  gallery: GalleryImage[];
  /** Persists the change (PATCH); resolves on success, rejects on failure. */
  onChange: (g: GalleryImage[]) => void | Promise<unknown>;
  title?: string;
  /** R2 key prefix for uploads (projects/gallery, units/gallery, ...) */
  prefix?: string;
  /** flat = render without the card chrome (for embedding in the unit editor) */
  flat?: boolean;
}) {
  const { uploading, uploadError, uploadSuccess, run } = useUpload();
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFiles(files: FileList) {
    run(async () => {
      const next = [...gallery];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("prefix", prefix);
        const res = await api<{ key: string; mimeType: string }>(
          "/api/admin/upload",
          { method: "POST", formData: fd },
        );
        next.push({
          key: res.key,
          alt: "",
          order: next.length,
          mimeType: res.mimeType,
        });
      }
      // Await the PATCH so success/error reflects actual persistence.
      await onChange(next);
    });
  }

  function remove(key: string) {
    run(async () => void (await onChange(gallery.filter((g) => g.key !== key))));
  }

  function move(key: string, dir: -1 | 1) {
    const idx = gallery.findIndex((g) => g.key === key);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= gallery.length) return;
    const next = [...gallery];
    [next[idx], next[target]] = [next[target], next[idx]];
    run(async () => void (await onChange(next.map((g, i) => ({ ...g, order: i })))));
  }

  const body = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          className={
            flat ? "font-semibold text-sm min-w-0" : "font-semibold text-lg min-w-0"
          }
        >
          {title}
        </h2>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className={flat ? "btn-secondary px-4 py-2" : "btn-secondary"}
        >
          <Upload size={16} />
          {uploading ? "Feltöltés..." : "Kép / videó hozzáadása"}
        </button>
      </div>
      {uploadError && (
        <p className="mt-2 text-sm text-red-600">{uploadError}</p>
      )}
      {uploadSuccess && (
        <p className="mt-2 text-sm text-green-600">Feltöltve ✓</p>
      )}

      {gallery.length === 0 ? (
        <p className="mt-4 text-sm text-brand-500">
          Még nincsenek képek vagy videók.
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {gallery.map((img, i) => (
            <div key={img.key} className="relative group">
              {isVideoMedia(img) ? (
                <div className="relative aspect-square w-full rounded-md border border-brand-200 overflow-hidden bg-black">
                  <video
                    src={fileUrl(img.key)}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute inset-0 grid place-items-center text-white/90 pointer-events-none">
                    <Play size={22} />
                  </span>
                </div>
              ) : (
                <img
                  src={fileUrl(img.key)}
                  alt={img.alt ?? ""}
                  className="aspect-square w-full object-cover rounded-md border border-brand-200"
                />
              )}
              <button
                type="button"
                onClick={() => remove(img.key)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 grid place-items-center hover:bg-red-600 transition-opacity"
                title="Törlés"
              >
                <X size={12} />
              </button>
              <div className="absolute bottom-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => move(img.key, -1)}
                  disabled={i === 0}
                  className="w-6 h-6 rounded bg-white/90 text-brand-700 grid place-items-center text-xs disabled:opacity-40"
                  title="Előrébb"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => move(img.key, 1)}
                  disabled={i === gallery.length - 1}
                  className="w-6 h-6 rounded bg-white/90 text-brand-700 grid place-items-center text-xs disabled:opacity-40"
                  title="Hátrébb"
                >
                  →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (flat) return <div>{body}</div>;
  return <section className="card p-6">{body}</section>;
}

// ─── Floorplans ────────────────────────────────────────────────────────────

function FloorplansSection({
  floorplans,
  onChange,
  title = "Alaprajzok",
  prefix = "projects/floorplans",
  flat = false,
  showDisplayMode = false,
}: {
  floorplans: Floorplan[];
  /** Persists the change (PATCH); resolves on success, rejects on failure. */
  onChange: (fps: Floorplan[]) => void | Promise<unknown>;
  title?: string;
  prefix?: string;
  flat?: boolean;
  /** Unit-level only: expose the per-floorplan "inline / click" display toggle. */
  showDisplayMode?: boolean;
}) {
  const { uploading, uploadError, uploadSuccess, run } = useUpload();
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFiles(files: FileList) {
    run(async () => {
      const next = [...floorplans];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("prefix", prefix);
        const res = await api<{ key: string; mimeType: string }>(
          "/api/admin/upload",
          { method: "POST", formData: fd },
        );
        next.push({
          key: res.key,
          label: file.name.replace(/\.[^.]+$/, ""),
          mimeType: res.mimeType,
          order: next.length,
        });
      }
      // Await the PATCH so success/error reflects actual persistence.
      await onChange(next);
    });
  }

  function remove(key: string) {
    run(async () => void (await onChange(floorplans.filter((g) => g.key !== key))));
  }

  // Fires per keystroke — kept as a direct (non-`run`) call so it doesn't spam
  // the success badge; persistence is still guaranteed by the parent override.
  function rename(key: string, label: string) {
    void onChange(floorplans.map((g) => (g.key === key ? { ...g, label } : g)));
  }

  // Per-floorplan display mode. PDFs can't render inline, so the toggle is
  // disabled for them and any attempt to set 'inline' is ignored on save.
  function setDisplayMode(key: string, mode: "inline" | "click") {
    void onChange(
      floorplans.map((g) => {
        if (g.key !== key) return g;
        const canInline = g.mimeType.startsWith("image/");
        return { ...g, displayMode: mode === "inline" && canInline ? "inline" : "click" };
      }),
    );
  }

  const Wrapper = flat ? "div" : "section";
  return (
    <Wrapper className={flat ? undefined : "card p-6"}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          className={
            flat ? "font-semibold text-sm min-w-0" : "font-semibold text-lg min-w-0"
          }
        >
          {title}
        </h2>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-secondary"
        >
          <Upload size={16} />
          {uploading ? "Feltöltés..." : "Alaprajzok hozzáadása"}
        </button>
      </div>
      {uploadError && (
        <p className="mt-2 text-sm text-red-600">{uploadError}</p>
      )}
      {uploadSuccess && (
        <p className="mt-2 text-sm text-green-600">Feltöltve ✓</p>
      )}

      {floorplans.length === 0 ? (
        <p className="mt-4 text-sm text-brand-500">
          Még nincsenek alaprajzok.
        </p>
      ) : (
        <ul className="mt-4 space-y-2">
          {floorplans.map((f) => {
            const isImage = f.mimeType.startsWith("image/");
            const inline = f.displayMode === "inline" && isImage;
            return (
              <li
                key={f.key}
                className="rounded-md border border-brand-200 p-3 space-y-2"
              >
                <div className="flex items-center gap-3">
                  <FileText size={20} className="text-brand-400 shrink-0" />
                  <input
                    type="text"
                    value={f.label}
                    onChange={(e) => rename(f.key, e.target.value)}
                    className="input flex-1"
                  />
                  <a
                    href={fileUrl(f.key)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-brand-500 hover:text-brand-900"
                  >
                    Megnyit
                  </a>
                  <button
                    type="button"
                    onClick={() => remove(f.key)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                {showDisplayMode && (
                  <div className="flex flex-wrap items-center gap-2 pl-8">
                    <span className="text-xs text-brand-500">Megjelenítés:</span>
                    <div className="inline-flex rounded-md border border-brand-200 overflow-hidden text-xs">
                      <button
                        type="button"
                        onClick={() => setDisplayMode(f.key, "inline")}
                        disabled={!isImage}
                        className={`px-3 py-1 transition-colors ${
                          inline
                            ? "bg-accent text-white"
                            : "bg-white text-brand-600 hover:bg-brand-50"
                        } ${!isImage ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        Egyből képként
                      </button>
                      <button
                        type="button"
                        onClick={() => setDisplayMode(f.key, "click")}
                        className={`px-3 py-1 transition-colors border-l border-brand-200 ${
                          !inline
                            ? "bg-accent text-white"
                            : "bg-white text-brand-600 hover:bg-brand-50"
                        }`}
                      >
                        Kattintásra nyílik
                      </button>
                    </div>
                    {!isImage && (
                      <span className="text-xs text-amber-600">
                        PDF nem jeleníthető meg inline – csak kép tölthető fel inline módhoz.
                      </span>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Wrapper>
  );
}

// ─── Unit number-field helpers ───────────────────────────────────────────
//
// Regexes + parse/sanitize helpers live in @/lib/numeric-input (shared with
// the floors-manager apartment form).

/** Compare two numeric strings by value so 65.5 === 65.50 (post-normalize). */
function numericEq(a: string | null, b: string | null): boolean {
  if (a == null || b == null) return a == null && b == null;
  return Number(a) === Number(b);
}

const FIELD_LABELS_HU: Record<string, string> = {
  label: "Címke",
  floor: "Emelet",
  rooms: "Szobaszám",
  areaSqm: "Alapterület (m²)",
  priceHuf: "Ár",
  balconyAreaSqm: "Erkély (m²)",
  terraceAreaSqm: "Terasz (m²)",
  gardenAreaSqm: "Kert (m²)",
  storageAreaSqm: "Tároló (m²)",
  parkingCount: "Parkoló (db)",
  sortOrder: "Sorrend",
  slug: "URL azonosító",
  status: "Státusz",
};

/** Turn an API/network failure into a readable Hungarian message. */
function unitErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      issues?: Array<{ path?: (string | number)[]; message?: string }>;
    } | null;
    const issue = body?.issues?.[0];
    if (issue) {
      const key = issue.path?.find((p) => typeof p === "string") as
        | string
        | undefined;
      const label = key ? (FIELD_LABELS_HU[key] ?? key) : null;
      const msg = issue.message ?? "érvénytelen érték";
      return label ? `${label}: ${msg}` : msg;
    }
    if (err.status === 404) {
      return "A lakás nem található – lehet, hogy időközben törölték. Frissítsd az oldalt.";
    }
    if (err.status === 401 || err.status === 403) {
      return "Nincs jogosultság a mentéshez. Jelentkezz be újra.";
    }
    return `Mentési hiba (HTTP ${err.status}).`;
  }
  return "Hálózati hiba a mentés során. Próbáld újra.";
}

/** Quick-row editable fields as display strings (so comma input survives). */
function draftFromUnit(u: ProjectUnit) {
  return {
    label: u.label,
    floor: u.floor == null ? "" : String(u.floor),
    rooms: u.rooms ?? "",
    areaSqm: u.areaSqm == null ? "" : String(u.areaSqm),
    priceHuf: u.priceHuf == null ? "" : String(u.priceHuf),
    // Kézi ár-felülírás (F-MANUAL-O25N9HIR): üres string → nincs felülírás
    // (a számított/tételes ár érvényes); szám → ez az ár mindenhol.
    priceOverrideHuf:
      u.priceOverrideHuf == null ? "" : String(u.priceOverrideHuf),
    status: u.status,
  };
}
type QuickDraft = ReturnType<typeof draftFromUnit>;

function quickRowPayload(d: QuickDraft): Partial<NewProjectUnit> {
  return {
    label: d.label.trim(),
    floor: parseIntOrNull(d.floor),
    rooms: d.rooms.trim() === "" ? null : d.rooms.trim(),
    areaSqm: parseDecimal(d.areaSqm),
    priceHuf: parseIntOrNull(d.priceHuf),
    priceOverrideHuf: parseIntOrNull(d.priceOverrideHuf),
    status: d.status,
  };
}

/**
 * A lakás ár-befolyásoló mezői, amiket a gyorssor (kiírt „tételes" ár) és a
 * teljes szerkesztő (Árazás blokk) EGYÜTT, ugyanabból az élő állapotból
 * olvas — így egyik helyen sem ragad be egy korábbi értékre a másik helyen
 * végzett szerkesztés miatt.
 */
type PricingDraft = {
  balconyAreaSqm: string;
  terraceAreaSqm: string;
  gardenAreaSqm: string;
  priceItems: PriceItemDraft[];
};

function pricingDraftFromUnit(u: ProjectUnit): PricingDraft {
  return {
    balconyAreaSqm: (u.balconyAreaSqm as unknown as string | null) ?? "",
    terraceAreaSqm: (u.terraceAreaSqm as unknown as string | null) ?? "",
    gardenAreaSqm: (u.gardenAreaSqm as unknown as string | null) ?? "",
    priceItems: (u.priceItems ?? []).map(draftFromPriceItem),
  };
}

// ─── Units ─────────────────────────────────────────────────────────────────

/** Registers a unit row's flush callback; returns the unregister cleanup. */
type RegisterUnitSaver = (
  id: string,
  saveIfDirty: () => Promise<void>,
) => () => void;
type ReportUnitDirty = (id: string, dirty: boolean) => void;

function UnitsSection({
  projectId,
  units,
  registerSaver,
  reportDirty,
  pricingDefaults,
}: {
  projectId: string;
  units: ProjectUnit[];
  registerSaver: RegisterUnitSaver;
  reportDirty: ReportUnitDirty;
  pricingDefaults: PricingDefaults | null;
}) {
  const qc = useQueryClient();
  const [sectionError, setSectionError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (data: Partial<NewProjectUnit>) =>
      api<ProjectUnit>(`/api/admin/projects/${projectId}/units`, {
        method: "POST",
        body: { ...data, projectId },
      }),
    onSuccess: () => {
      setSectionError(null);
      qc.invalidateQueries({ queryKey: ["admin", "project", projectId] });
    },
    onError: (err) => setSectionError(unitErrorMessage(err)),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/units/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setSectionError(null);
      qc.invalidateQueries({ queryKey: ["admin", "project", projectId] });
    },
    onError: (err) => setSectionError(unitErrorMessage(err)),
  });

  // Duplikálás: a szerver klónozza a forrás lakás teljes tartalmát (a
  // másolat közvetlenül a forrás mögé kerül), így a nagy galéria-listákat
  // nem kell a kliensen átküldeni. Rövid zöld visszajelzés a minta szerint.
  const [duplicatedLabel, setDuplicatedLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!duplicatedLabel) return;
    const t = setTimeout(() => setDuplicatedLabel(null), 2500);
    return () => clearTimeout(t);
  }, [duplicatedLabel]);

  const duplicate = useMutation({
    mutationFn: (id: string) =>
      api<ProjectUnit>(`/api/admin/units/${id}/duplicate`, { method: "POST" }),
    onSuccess: (row) => {
      setSectionError(null);
      setDuplicatedLabel(row.label);
      qc.invalidateQueries({ queryKey: ["admin", "project", projectId] });
    },
    onError: (err) => setSectionError(unitErrorMessage(err)),
  });

  // A nyilakkal történő átrendezés a teljes lista normalizált sorrendjét
  // menti (index alapú sortOrder), így az azonos sortOrder-ű lakások is
  // determinisztikus sorrendbe kerülnek — a floors/reorder mintájára.
  const reorder = useMutation({
    mutationFn: (order: { id: string; sortOrder: number }[]) =>
      api("/api/admin/units/reorder", { method: "POST", body: { order } }),
    onSuccess: () => {
      setSectionError(null);
      qc.invalidateQueries({ queryKey: ["admin", "project", projectId] });
    },
    onError: (err) => setSectionError(unitErrorMessage(err)),
  });

  function move(idx: number, delta: -1 | 1) {
    const target = idx + delta;
    if (target < 0 || target >= units.length) return;
    const next = [...units];
    [next[idx], next[target]] = [next[target], next[idx]];
    reorder.mutate(next.map((u, i) => ({ id: u.id, sortOrder: i })));
  }

  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <section className="card p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-lg">Lakások</h2>
          <p className="text-xs text-brand-500 mt-0.5">
            A ✏️ gombbal nyílik a teljes szerkesztő: leírás, tájolás, erkély,
            parkoló, galéria, alaprajz, virtuális séta, stílusvariánsok.
          </p>
        </div>
        <button
          type="button"
          disabled={create.isPending}
          onClick={() =>
            create.mutate({
              label: `Új lakás ${units.length + 1}`,
              status: "available",
              sortOrder: units.length,
            })
          }
          className="btn-secondary"
        >
          <Plus size={16} />
          Új lakás
        </button>
      </div>

      {sectionError && (
        <div className="mt-3 text-sm text-red-600">{sectionError}</div>
      )}
      {duplicatedLabel && (
        <div className="mt-3 inline-flex items-center gap-1 text-sm text-green-600">
          <Check size={14} /> Lakás duplikálva: {duplicatedLabel}
        </div>
      )}

      {units.length === 0 ? (
        <p className="mt-4 text-sm text-brand-500">Még nincsenek lakások.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-brand-500">
              <tr>
                <th className="px-1 py-2" title="Sorrend"></th>
                <th className="text-left px-2 py-2">Címke</th>
                <th className="text-left px-2 py-2">Em.</th>
                <th className="text-left px-2 py-2">Szoba</th>
                <th className="text-left px-2 py-2">m²</th>
                <th className="text-left px-2 py-2">Ár (Ft)</th>
                <th className="text-left px-2 py-2">Státusz</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {units.map((u, idx) => (
                <UnitRow
                  key={u.id}
                  unit={u}
                  projectId={projectId}
                  registerSaver={registerSaver}
                  reportDirty={reportDirty}
                  pricingDefaults={pricingDefaults}
                  expanded={expandedId === u.id}
                  onToggleExpand={() =>
                    setExpandedId(expandedId === u.id ? null : u.id)
                  }
                  onDelete={() => {
                    if (confirm(`Törli ezt: ${u.label}?`)) del.mutate(u.id);
                  }}
                  onDuplicate={() => duplicate.mutate(u.id)}
                  duplicating={duplicate.isPending}
                  onMoveUp={() => move(idx, -1)}
                  onMoveDown={() => move(idx, 1)}
                  canMoveUp={idx > 0 && !reorder.isPending}
                  canMoveDown={idx < units.length - 1 && !reorder.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─── Projekt-szintű árazási alapértékek ──────────────────────────────────

/**
 * Projekt-szintű „Hamarosan” mód. Ugyanaz a logika és ugyanazok a mezők, mint
 * az oldal-szintű kapcsolónál (közös `MaintenanceFields`), csak egyetlen
 * projektre. A mentés azonnal a projektre ír — mint az árazási alapértékeknél.
 */
function ProjectMaintenanceSection({
  value,
  onSave,
}: {
  value: MaintenanceSettings | null;
  onSave: (v: MaintenanceSettings) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<MaintenanceSettings>(() => value ?? {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(value ?? {});
  }, [value]);

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(t);
  }, [saved]);

  function save() {
    setSaving(true);
    setSaved(false);
    Promise.resolve(onSave(draft))
      .then(() => setSaved(true))
      .finally(() => setSaving(false));
  }

  return (
    <section className="card p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-lg">Projekt állapota</h2>
        <p className="text-xs text-brand-500 mt-0.5">
          Éles a projekt, vagy „Hamarosan” állapotban van? Bekapcsolva a projekt
          a listában marad, de a tartalma (leírás, galéria, alaprajzok, lakások,
          árak) rejtve marad — Ön adminként továbbra is látja és javíthatja.
          Ha teljesen el akarja tüntetni a projektet, vegye ki a „Publikálva”
          pipát az alapadatoknál.
        </p>
      </div>

      <MaintenanceFields
        value={draft}
        onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        scope="project"
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary"
        >
          {saving ? "Mentés…" : "Állapot mentése"}
        </button>
        {saved && !saving && (
          <span className="inline-flex items-center gap-1 text-sm text-green-600">
            <Check size={15} /> Mentve
          </span>
        )}
      </div>
    </section>
  );
}

function PricingDefaultsSection({
  value,
  onSave,
}: {
  value: PricingDefaults | null;
  onSave: (v: PricingDefaults) => Promise<unknown>;
}) {
  const seed = (v: PricingDefaults | null) => ({
    baseSqmPriceHuf: v?.baseSqmPriceHuf != null ? String(v.baseSqmPriceHuf) : "",
    outdoorSqmPriceHuf:
      v?.outdoorSqmPriceHuf != null ? String(v.outdoorSqmPriceHuf) : "",
    vatPct: v?.vatPct != null ? String(v.vatPct) : "",
    isGross: v?.isGross ?? true,
    plusVat: v?.plusVat ?? false,
    hidePrices: v?.hidePrices ?? false,
  });
  const [draft, setDraft] = useState(() => seed(value));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Re-seed when the loaded project (or a save's normalized result) changes.
  useEffect(() => {
    setDraft(seed(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed is pure
  }, [value]);

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(t);
  }, [saved]);

  function setInt(key: "baseSqmPriceHuf" | "outdoorSqmPriceHuf", v: string) {
    const s = sanitizeInt(v);
    if (INT_INPUT_RE.test(s)) setDraft((d) => ({ ...d, [key]: s }));
  }
  function setVat(v: string) {
    const s = sanitizeDecimal(v);
    if (DECIMAL_INPUT_RE.test(s)) setDraft((d) => ({ ...d, vatPct: s }));
  }

  // A mentetlen draftból számolva, hogy a figyelmeztetés élőben kövesse a
  // beírt / kitörölt referencia árakat.
  const hiddenByEmptyRefs =
    (parseIntOrNull(draft.baseSqmPriceHuf) ?? 0) <= 0 &&
    (parseIntOrNull(draft.outdoorSqmPriceHuf) ?? 0) <= 0;

  function save() {
    setSaving(true);
    setSaved(false);
    Promise.resolve(
      onSave({
        baseSqmPriceHuf: parseIntOrNull(draft.baseSqmPriceHuf),
        outdoorSqmPriceHuf: parseIntOrNull(draft.outdoorSqmPriceHuf),
        vatPct:
          draft.vatPct.trim() === ""
            ? null
            : Number(parseDecimal(draft.vatPct) ?? 0),
        isGross: draft.isGross,
        plusVat: draft.plusVat,
        hidePrices: draft.hidePrices,
      }),
    )
      .then(() => setSaved(true))
      .finally(() => setSaving(false));
  }

  return (
    <section className="card p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-lg">Árazási alapértékek</h2>
        <p className="text-xs text-brand-500 mt-0.5">
          Új lakás ár-tételeinek előtöltéséhez. Lakásonként felülírható.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 field-grid">
        <div>
          <label className="label">Alap m²-ár (Ft/m²)</label>
          <input
            type="text"
            inputMode="numeric"
            className="input text-right"
            placeholder="pl. 1 200 000"
            value={draft.baseSqmPriceHuf}
            onChange={(e) => setInt("baseSqmPriceHuf", e.target.value)}
          />
        </div>
        <div>
          <label className="label">Erkély / terasz m²-ár (Ft/m²)</label>
          <input
            type="text"
            inputMode="numeric"
            className="input text-right"
            placeholder="pl. 600 000"
            value={draft.outdoorSqmPriceHuf}
            onChange={(e) => setInt("outdoorSqmPriceHuf", e.target.value)}
          />
        </div>
        <div>
          <label className="label">ÁFA-kulcs (%)</label>
          <input
            type="text"
            inputMode="decimal"
            className="input text-right"
            placeholder="pl. 5"
            value={draft.vatPct}
            onChange={(e) => setVat(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.isGross}
            onChange={(e) => setDraft((d) => ({ ...d, isGross: e.target.checked }))}
          />
          Az alap-egységárak bruttók
        </label>
        <label
          className={`flex items-center gap-2 text-sm ${
            draft.isGross ? "text-brand-300" : ""
          }`}
        >
          <input
            type="checkbox"
            disabled={draft.isGross}
            checked={draft.plusVat}
            onChange={(e) => setDraft((d) => ({ ...d, plusVat: e.target.checked }))}
          />
          Nettó árnál +ÁFA értendő
        </label>
      </div>

      {/* Publikus ár-elrejtés. Két úton kapcsolható (shared/pricing.ts
          `arePricesHidden`): a referencia m²-árak törlésével, vagy ezzel a
          pipával — utóbbi bármikor visszavonható. A lakásoknál beírt árak és a
          tételek NEM vesznek el, csak nem látszanak a publikus oldalon. */}
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={draft.hidePrices}
          onChange={(e) =>
            setDraft((d) => ({ ...d, hidePrices: e.target.checked }))
          }
        />
        <span>
          Ne jelenjenek meg árak a publikus oldalon — „Kérjen ajánlatot"
          <span className="block text-xs text-brand-500">
            Az adatok megmaradnak, a pipa kivételével azonnal visszatérnek.
          </span>
        </span>
      </label>

      {hiddenByEmptyRefs && !draft.hidePrices && (
        <p className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded">
          Nincs megadva referencia m²-ár, ezért a projekt lakásainál a publikus
          oldalon jelenleg NEM látszik ár — az ár helyén „Kérjen ajánlatot" áll
          (az admin felületen minden számított érték változatlanul látható).
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary"
        >
          {saving ? "Mentés…" : "Alapértékek mentése"}
        </button>
        {saved && !saving && (
          <span className="inline-flex items-center gap-1 text-sm text-green-600">
            <Check size={15} /> Mentve
          </span>
        )}
      </div>
    </section>
  );
}

function UnitRow({
  unit,
  projectId,
  registerSaver,
  reportDirty,
  pricingDefaults,
  expanded,
  onToggleExpand,
  onDelete,
  onDuplicate,
  duplicating,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  unit: ProjectUnit;
  projectId: string;
  registerSaver: RegisterUnitSaver;
  reportDirty: ReportUnitDirty;
  pricingDefaults: PricingDefaults | null;
  expanded: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  duplicating: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<QuickDraft>(() => draftFromUnit(unit));
  const [saved, setSaved] = useState(false);
  // Az erkély/terasz/kert m² és az ár-tételek élő állapota — a gyorssor
  // (ez az érték) és a teljes szerkesztő (lentebb, expanded állapotban)
  // KÖZÖSEN ezt olvassa/írja, hogy egyik se ragadjon a másik szerkesztése alatt.
  const [pricingDraft, setPricingDraft] = useState<PricingDraft>(() =>
    pricingDraftFromUnit(unit),
  );

  // This row owns its own save mutation so success / error feedback and the
  // post-save draft reset are scoped to *this* unit (a shared mutation can't
  // tell which row failed). Both the quick row and the full editor save
  // through it.
  const save = useMutation({
    mutationFn: (data: Partial<NewProjectUnit>) =>
      api<ProjectUnit>(`/api/admin/units/${unit.id}`, {
        method: "PATCH",
        body: data,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "project", projectId] });
      setSaved(true);
    },
  });

  // Auto-hide the "Mentve" confirmation after a couple of seconds.
  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(t);
  }, [saved]);

  const dirty =
    draft.label.trim() !== unit.label ||
    parseIntOrNull(draft.floor) !== (unit.floor ?? null) ||
    (draft.rooms.trim() === "" ? null : draft.rooms.trim()) !==
      (unit.rooms ?? null) ||
    !numericEq(
      parseDecimal(draft.areaSqm),
      (unit.areaSqm as unknown as string | null) ?? null,
    ) ||
    parseIntOrNull(draft.priceHuf) !== (unit.priceHuf ?? null) ||
    parseIntOrNull(draft.priceOverrideHuf) !==
      (unit.priceOverrideHuf ?? null) ||
    draft.status !== unit.status;

  // ── Big-"Mentés" integration ──────────────────────────────────────────────
  // The page-level save button flushes this row's draft through the registry.
  // Refs keep the callback registered once per row while always seeing the
  // latest draft / dirty / pending state.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const pendingRef = useRef(save.isPending);
  pendingRef.current = save.isPending;

  useEffect(() => {
    const { mutateAsync } = save;
    return registerSaver(unit.id, async () => {
      if (!dirtyRef.current || pendingRef.current) return;
      // mutateAsync reuses this row's mutation, so pending / error / "Mentve"
      // feedback behaves exactly like the row's own Mentés button. A failure
      // rejects (not swallowed) and stays visible in the row's error line.
      const fresh = await mutateAsync(quickRowPayload(draftRef.current));
      setDraft(draftFromUnit(fresh));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutateAsync is stable
  }, [unit.id, registerSaver]);

  // Report dirtiness so the big Mentés button enables when only rows changed;
  // clear the flag when the row unmounts (e.g. the unit gets deleted).
  useEffect(() => {
    reportDirty(unit.id, dirty);
  }, [unit.id, dirty, reportDirty]);
  useEffect(() => {
    return () => reportDirty(unit.id, false);
  }, [unit.id, reportDirty]);

  function saveRow() {
    // An empty label is rejected server-side (min 1) and surfaces in the error
    // row below — no silent no-op.
    save.mutate(quickRowPayload(draft), {
      // Re-seed the draft from the server's canonical row so numeric
      // normalization (65.5 → 65.50) doesn't leave the row stuck "dirty".
      onSuccess: (fresh) => setDraft(draftFromUnit(fresh)),
    });
  }

  const errorMsg = save.error ? unitErrorMessage(save.error) : null;

  // A gyorssor „Ár" oszlopa: a SZÁMÍTOTT (tételes/fallback) árat élőben az összes
  // ár-befolyásoló mezőből számoljuk (ez + pricingDraft), a kézi felülírás NÉLKÜL
  // — így a „számított" hint / a „vissza a számítotthoz" célérték mindig a friss
  // tételes ár. A kézi felülírás (priceOverrideHuf) külön, a draftban él, és ha
  // meg van adva, az NYER (lásd a cella JSX-ét + computeUnitPrice).
  const autoPrice = computeUnitPrice({
    priceHuf: unit.priceHuf,
    areaSqm: draft.areaSqm === "" ? null : draft.areaSqm,
    balconyAreaSqm:
      pricingDraft.balconyAreaSqm === "" ? null : pricingDraft.balconyAreaSqm,
    terraceAreaSqm:
      pricingDraft.terraceAreaSqm === "" ? null : pricingDraft.terraceAreaSqm,
    gardenAreaSqm:
      pricingDraft.gardenAreaSqm === "" ? null : pricingDraft.gardenAreaSqm,
    priceItems: pricingDraft.priceItems.map(priceItemFromDraft),
    // Alapérték-alapú m²-árazás: ha a lakásnak nincs saját m²-alaptétele, a
    // projekt alapértékeiből (élő) számol → a m² átírása azonnal újraszámol.
    pricingDefaults,
  });
  const hasOverride = draft.priceOverrideHuf.trim() !== "";
  const setOverride = (v: string) =>
    setDraft((d) => ({ ...d, priceOverrideHuf: v }));

  return (
    <>
      <tr className="border-t border-brand-200">
        <td className="px-1 py-2 w-8">
          <div className="flex flex-col items-center gap-0.5">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              className="text-brand-400 hover:text-brand-700 disabled:opacity-30"
              title="Mozgatás felfelé"
            >
              <ArrowUp size={14} />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              className="text-brand-400 hover:text-brand-700 disabled:opacity-30"
              title="Mozgatás lefelé"
            >
              <ArrowDown size={14} />
            </button>
          </div>
        </td>
        <td className="px-2 py-2">
          <input
            className="input px-2.5 py-2 min-w-[7rem]"
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          />
        </td>
        <td className="px-2 py-2 w-16">
          <input
            type="text"
            inputMode="numeric"
            className="input px-2.5 py-2 min-w-[3.5rem]"
            value={draft.floor}
            onChange={(e) => {
              const v = sanitizeInt(e.target.value);
              if (INT_INPUT_RE.test(v)) setDraft({ ...draft, floor: v });
            }}
          />
        </td>
        <td className="px-2 py-2 w-20">
          <input
            className="input px-2.5 py-2 min-w-[4rem]"
            value={draft.rooms}
            onChange={(e) => setDraft({ ...draft, rooms: e.target.value })}
          />
        </td>
        <td className="px-2 py-2 w-20">
          <input
            type="text"
            inputMode="decimal"
            className="input px-2.5 py-2 min-w-[4.5rem] text-right"
            value={draft.areaSqm}
            onChange={(e) => {
              const v = sanitizeDecimal(e.target.value);
              if (DECIMAL_INPUT_RE.test(v)) setDraft({ ...draft, areaSqm: v });
            }}
          />
        </td>
        <td className="px-2 py-2 w-40">
          {autoPrice.hasItems ? (
            // Tételes lakás: kézi ár-felülírás mező (üresen a tételes számított
            // ár érvényes, élő újraszámítással); alatta a számított érték hintje
            // és — kézi érték esetén — a „vissza a számítotthoz" gomb.
            <div className="space-y-1">
              <input
                type="text"
                inputMode="numeric"
                className="input px-2.5 py-1.5 min-w-[7rem] text-right"
                placeholder={autoPrice.grossHuf.toLocaleString("hu-HU")}
                value={draft.priceOverrideHuf}
                onChange={(e) => {
                  const v = sanitizeInt(e.target.value);
                  if (INT_INPUT_RE.test(v)) setOverride(v);
                }}
                title="Kézi ár-felülírás (Ft). Üresen a tételes számított ár érvényes."
              />
              <div className="text-[10px] text-right">
                {hasOverride ? (
                  <button
                    type="button"
                    onClick={() => setOverride("")}
                    className="text-accent hover:underline inline-flex items-center gap-0.5"
                    title="Kézi ár törlése — vissza a tételes számított árhoz"
                  >
                    <RotateCcw size={10} /> számított:{" "}
                    {formatPriceHuf(autoPrice.grossHuf)}
                  </button>
                ) : (
                  <span className="text-brand-400">
                    tételes: {formatPriceHuf(autoPrice.grossHuf)}
                  </span>
                )}
              </div>
            </div>
          ) : (
            // Fallback (tétel nélküli) lakás: a priceHuf marad a közvetlen,
            // kézzel írható ár-mező (visszafelé kompatibilitás).
            <input
              type="text"
              inputMode="numeric"
              className="input px-2.5 py-2 min-w-[7rem] text-right"
              value={draft.priceHuf}
              onChange={(e) => {
                const v = sanitizeInt(e.target.value);
                if (INT_INPUT_RE.test(v)) setDraft({ ...draft, priceHuf: v });
              }}
            />
          )}
        </td>
        <td className="px-2 py-2 w-32">
          <select
            className="input px-2.5 py-2 min-w-[6.5rem]"
            value={draft.status}
            onChange={(e) =>
              setDraft({
                ...draft,
                status: e.target.value as ProjectUnit["status"],
              })
            }
          >
            <option value="available">Szabad</option>
            <option value="reserved">Foglalt</option>
            <option value="sold">Eladott</option>
          </select>
        </td>
        <td className="px-2 py-2 text-right whitespace-nowrap">
          {save.isPending ? (
            <span className="text-xs text-brand-500 mr-2">Mentés…</span>
          ) : dirty ? (
            <button
              type="button"
              onClick={saveRow}
              className="text-xs px-2 py-1 bg-brand-900 text-white rounded mr-2 hover:bg-brand-800"
            >
              Mentés
            </button>
          ) : saved ? (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 mr-2">
              <Check size={13} /> Mentve
            </span>
          ) : null}
          <button
            type="button"
            onClick={onToggleExpand}
            className={`mr-2 inline-flex items-center justify-center w-7 h-7 rounded ${
              expanded
                ? "bg-brand-900 text-white"
                : "text-brand-500 hover:text-brand-900 hover:bg-brand-100"
            }`}
            title={expanded ? "Szerkesztő bezárása" : "Teljes szerkesztés"}
          >
            {expanded ? <ChevronUp size={15} /> : <Pencil size={15} />}
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            disabled={duplicating}
            className="mr-2 text-brand-500 hover:text-brand-900 disabled:opacity-40"
            title="Lakás duplikálása"
          >
            <Copy size={15} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 size={16} />
          </button>
        </td>
      </tr>
      {errorMsg && (
        <tr className="bg-red-50">
          <td colSpan={8} className="px-2 py-1.5 text-xs text-red-600">
            {errorMsg}
          </td>
        </tr>
      )}
      {expanded && (
        <tr className="border-t border-brand-200 bg-brand-50/60">
          <td colSpan={8} className="px-2 py-4 sm:px-4">
            <UnitFullEditor
              unit={unit}
              onSave={(data) => save.mutate(data)}
              saving={save.isPending}
              saved={saved}
              pricingDefaults={pricingDefaults}
              liveAreaSqm={draft.areaSqm}
              pricingDraft={pricingDraft}
              onPricingDraftChange={setPricingDraft}
              priceOverrideDraft={draft.priceOverrideHuf}
              onPriceOverrideChange={setOverride}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Árazás (tételes, ÁFA-tudatos) ───────────────────────────────────────

/** Ár-tétel szerkesztési draftja — a számmezők stringek (magyar vessző). */
type PriceItemDraft = {
  kind: PriceItemKind;
  /** m²-kötés (F-MANUAL-TSNIZ4OY): ha be van állítva, a darabszám a lakás
   *  friss m² mezőjéből jön (élő újraszámítás), nem a tárolt quantity-ből. */
  boundToM2: BoundToM2 | null;
  label: string;
  unitPriceHuf: string;
  quantity: string;
  /** Extra mennyiség-egysége: darab vagy m². */
  unit: "db" | "sqm";
  isGross: boolean;
  vatPct: string;
  plusVat: boolean;
  optional: boolean;
};

function draftFromPriceItem(it: PriceItem): PriceItemDraft {
  return {
    kind: it.kind,
    boundToM2: it.boundToM2 ?? null,
    label: it.label ?? "",
    unitPriceHuf: it.unitPriceHuf ? String(it.unitPriceHuf) : "",
    quantity: it.quantity != null ? String(it.quantity) : "1",
    unit: it.unit === "sqm" ? "sqm" : "db",
    isGross: it.isGross ?? true,
    vatPct: it.vatPct != null ? String(it.vatPct) : "",
    plusVat: it.plusVat ?? false,
    optional: it.optional ?? true,
  };
}

/** Draft → API PriceItem (számokra parse-olva). */
function priceItemFromDraft(d: PriceItemDraft): PriceItem {
  const item: PriceItem = {
    kind: d.kind,
    unitPriceHuf: parseIntOrNull(d.unitPriceHuf) ?? 0,
    isGross: d.isGross,
    vatPct: Number(parseDecimal(d.vatPct) ?? 0),
    plusVat: d.plusVat,
  };
  // m²-kötött tétel: a darabszám compute-időben a lakás m²-éből jön, ezért nem
  // mentünk quantity-t; a kötést megőrizzük.
  if (d.boundToM2) {
    item.boundToM2 = d.boundToM2;
    if (d.label.trim()) item.label = d.label.trim();
    return item;
  }
  if (d.kind === "extra") {
    const label = d.label.trim();
    if (label) item.label = label;
    item.unit = d.unit;
    item.quantity =
      d.unit === "sqm"
        ? Number(parseDecimal(d.quantity) ?? 0)
        : (parseIntOrNull(d.quantity) ?? 1);
    item.optional = d.optional;
  } else if (d.label.trim()) {
    item.label = d.label.trim();
  }
  return item;
}

/**
 * A tétel-hozzáadó gombsor — csak a nevesített extrák (mindig opcionálisak,
 * a lakás árához nem adódnak hozzá). Az alap tételek (alapterület, erkély,
 * terasz — az alapár részei) a „Kitöltés alapértékekből" gombbal jönnek létre
 * a lakás m² mezői alapján. A „Kert" a megrendelő kérésére nem választható —
 * meglévő kert-tétel továbbra is renderelődik és számolódik.
 */
const ADD_BUTTONS: {
  kind: PriceItemKind;
  label: string;
  presetLabel?: string;
  /** Előtöltött mennyiség-egység — a Tároló m²-alapú (méret × m²-ár). */
  presetUnit?: "db" | "sqm";
}[] = [
  { kind: "extra", label: "Garázs", presetLabel: "Garázs" },
  { kind: "extra", label: "Kültéri parkoló", presetLabel: "Kültéri parkoló" },
  { kind: "extra", label: "Tároló", presetLabel: "Tároló", presetUnit: "sqm" },
  { kind: "extra", label: "Egyéb extra" },
];

/**
 * A lakás ár-tételeinek szerkesztője élő nettó + ÁFA + bruttó összesítővel.
 * A m²-tételek mennyisége a lakás m² mezőiből jön (a `areas` propból), ezért a
 * base_sqm-hoz a (gyorssorban szerkesztett) alapterületet is átadjuk.
 */
function UnitPricingEditor({
  items,
  onChange,
  areas,
  priceHuf,
  priceOverrideDraft,
  onPriceOverrideChange,
  defaults,
}: {
  items: PriceItemDraft[];
  onChange: (items: PriceItemDraft[]) => void;
  areas: {
    areaSqm: string | number | null;
    balconyAreaSqm: string;
    terraceAreaSqm: string;
    gardenAreaSqm: string;
  };
  priceHuf: number | null;
  /** Kézi ár-felülírás élő draftja (a UnitRow-ból) — közös a gyorssorral. */
  priceOverrideDraft: string;
  onPriceOverrideChange: (v: string) => void;
  defaults: PricingDefaults | null;
}) {
  const parsed = items.map(priceItemFromDraft);
  // A tételes/fallback SZÁMÍTOTT ár (a kézi felülírás NÉLKÜL) — ez az élő
  // összesítő és a „vissza a számítotthoz" célértéke.
  const preview = computeUnitPrice({
    priceHuf,
    areaSqm: areas.areaSqm,
    balconyAreaSqm: areas.balconyAreaSqm,
    terraceAreaSqm: areas.terraceAreaSqm,
    gardenAreaSqm: areas.gardenAreaSqm,
    priceItems: parsed,
    // Alapérték-alapú m²-árazás (mint a gyorssorban): saját m²-tétel hiányában
    // a projekt alapértékeiből számol, így a m² átírása élőben újraszámol.
    pricingDefaults: defaults,
  });
  const hasOverride = priceOverrideDraft.trim() !== "";

  // Per-row amounts computed independently of `preview.lines` — the aggregate
  // filters out zero-priced items, so its line indices would misalign with a
  // freshly-added (still empty) row. Each row derives its own quantity + gross.
  const areaNum = (v: string | number | null | undefined): number => {
    if (v == null) return 0;
    const n =
      typeof v === "number"
        ? v
        : Number(String(v).replace(/\s+/g, "").replace(/,/g, "."));
    return Number.isFinite(n) ? n : 0;
  };
  // m²-kötött tétel élő mennyisége a friss m² mezőkből (F-MANUAL-TSNIZ4OY).
  const boundQty = (bound: BoundToM2): number =>
    bound === "area"
      ? areaNum(areas.areaSqm)
      : areaNum(areas.balconyAreaSqm) + areaNum(areas.terraceAreaSqm);
  const areaForKind = (kind: PriceItemKind): number => {
    switch (kind) {
      case "base_sqm":
        return areaNum(areas.areaSqm);
      case "balcony_sqm":
        return areaNum(areas.balconyAreaSqm);
      case "terrace_sqm":
        return areaNum(areas.terraceAreaSqm);
      case "garden_sqm":
        return areaNum(areas.gardenAreaSqm);
      default:
        return 0;
    }
  };
  const rowLine = (draft: PriceItemDraft) => {
    const item = priceItemFromDraft(draft);
    // A m²-kötés NYER: a darabszám a friss m²-ből; egyébként a régi kötés.
    const quantity = draft.boundToM2
      ? boundQty(draft.boundToM2)
      : isSqmKind(item.kind)
        ? areaForKind(item.kind)
        : item.unit === "sqm"
          ? Math.max(0, item.quantity ?? 0)
          : Math.max(0, Math.trunc(item.quantity ?? 1));
    return { quantity, grossHuf: computeLineAmounts(item, quantity).grossHuf };
  };

  function addItem(
    kind: PriceItemKind,
    presetLabel?: string,
    presetUnit?: "db" | "sqm",
  ) {
    const draft = draftFromPriceItem(defaultPriceItem(kind, defaults, presetLabel));
    if (presetUnit === "sqm") {
      // m²-alapú extra: a mennyiséget (m²) az admin adja meg, üresen indul.
      draft.unit = "sqm";
      draft.quantity = "";
    }
    onChange([...items, draft]);
  }
  function updateItem(index: number, patch: Partial<PriceItemDraft>) {
    onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }
  function removeItem(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }
  // Az alap (m²-kötött) tételek — ezek adják a lakás árát a friss m²-ből. A
  // gombsorból nem vehetők fel; a „Kitöltés alapértékekből" pótolja a hiányzó
  // kötéseket (alapterület + erkély/terasz) a meglévő sorok megtartásával. A
  // régi, kötés nélküli *_sqm tételeket is „lefedettnek" tekintjük, hogy ne
  // keletkezzen duplikátum a korábban létrehozott lakásoknál.
  const hasArea = items.some(
    (i) => i.boundToM2 === "area" || (!i.boundToM2 && i.kind === "base_sqm"),
  );
  const hasBalconyTerrace = items.some(
    (i) =>
      i.boundToM2 === "balconyTerrace" ||
      (!i.boundToM2 && (i.kind === "balcony_sqm" || i.kind === "terrace_sqm")),
  );
  // Az erkély/terasz kötést csak akkor ajánljuk fel, ha van rá értelmes ok:
  // be van állítva az erkély/terasz m²-ár, vagy van erkély/terasz m².
  const balconyTerraceRelevant =
    areaNum(defaults?.outdoorSqmPriceHuf) > 0 || boundQty("balconyTerrace") > 0;
  const missingBounds: BoundToM2[] = [];
  if (!hasArea) missingBounds.push("area");
  if (!hasBalconyTerrace && balconyTerraceRelevant)
    missingBounds.push("balconyTerrace");
  function prefill() {
    onChange([
      ...items,
      ...missingBounds.map((bound) =>
        draftFromPriceItem(boundPriceItem(bound, defaults)),
      ),
    ]);
  }

  return (
    <div className="rounded-lg border border-brand-200 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-semibold text-sm">Árazás (tételes)</h4>
          <p className="text-xs text-brand-500 mt-0.5">
            A lakás ára a tételekből számolódik. Tételek nélkül a gyorssor „Ár
            (Ft)” mezője (fallback) érvényes.
          </p>
        </div>
        {missingBounds.length > 0 && (
          <button
            type="button"
            onClick={prefill}
            className="btn-secondary !py-1.5 !text-xs whitespace-nowrap"
          >
            Kitöltés alapértékekből
          </button>
        )}
      </div>

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((it, i) => (
            <PriceItemRow
              key={i}
              draft={it}
              line={rowLine(it)}
              onChange={(patch) => updateItem(i, patch)}
              onRemove={() => removeItem(i)}
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {ADD_BUTTONS.map((btn) => (
          <button
            key={btn.label}
            type="button"
            onClick={() => addItem(btn.kind, btn.presetLabel, btn.presetUnit)}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-brand-200 text-brand-700 hover:bg-brand-50"
          >
            <Plus size={13} /> {btn.label}
          </button>
        ))}
      </div>

      {/* Élő összesítő */}
      {preview.hasItems && (
        <div className="rounded-md bg-brand-50 border border-brand-200 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-brand-500">Nettó (alapár):</span>
            <span className="tabular-nums">{formatPriceHuf(preview.netHuf)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-brand-500">ÁFA:</span>
            <span className="tabular-nums">{formatPriceHuf(preview.vatHuf)}</span>
          </div>
          <div className="flex items-center justify-between font-semibold border-t border-brand-200 mt-1.5 pt-1.5">
            <span>Bruttó alapár:</span>
            <span className="tabular-nums text-brand-800">
              {formatPriceHuf(preview.grossHuf)}
            </span>
          </div>
          {preview.lines.some((l) => l.optional) && (
            <div className="mt-1.5 pt-1.5 border-t border-brand-200">
              <div className="text-xs text-brand-400 mb-1">
                Opcionális extrák — nem részei a lakás árának:
              </div>
              {preview.lines
                .filter((l) => l.optional)
                .map((l, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-brand-500 text-xs"
                  >
                    <span>
                      {l.label}
                      {l.unit === "sqm"
                        ? ` (${l.quantity.toLocaleString("hu-HU")} m²)`
                        : l.quantity > 1
                          ? ` (${l.quantity} db)`
                          : ""}
                    </span>
                    <span className="tabular-nums">
                      {formatPriceHuf(l.grossHuf)}
                      <span className="text-brand-400">
                        {" "}
                        ({l.vatPct}% ÁFA)
                      </span>
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Kézi ár-felülírás — minden lakásnál (tételes / fallback / másolt).
          Üresen a fenti számított ár érvényes; érték megadva ez NYER, és ez a
          bruttó jelenik meg mindenhol (F-MANUAL-O25N9HIR). */}
      <div className="rounded-md border border-brand-200 p-3 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h4 className="font-semibold text-sm">Kézi ár-felülírás</h4>
            <p className="text-xs text-brand-500 mt-0.5">
              Üresen a számított ár érvényes (élő újraszámítással). Érték megadva
              ez a bruttó ár jelenik meg mindenhol — a lakásoldalon, az
              összehasonlításban és a hitelkalkulátorban is.
            </p>
          </div>
          {hasOverride && (
            <button
              type="button"
              onClick={() => onPriceOverrideChange("")}
              className="btn-secondary !py-1.5 !text-xs inline-flex items-center gap-1 whitespace-nowrap"
            >
              <RotateCcw size={13} /> Vissza a számítotthoz
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            inputMode="numeric"
            className="input px-2.5 py-2 max-w-[13rem] text-right"
            placeholder={`számított: ${preview.grossHuf.toLocaleString("hu-HU")}`}
            value={priceOverrideDraft}
            onChange={(e) => {
              const v = sanitizeInt(e.target.value);
              if (INT_INPUT_RE.test(v)) onPriceOverrideChange(v);
            }}
          />
          <span className="text-xs text-brand-400">Ft (bruttó)</span>
        </div>
        <div className="text-xs">
          {hasOverride ? (
            <span className="text-brand-600">
              Érvényes ár:{" "}
              <span className="font-semibold tabular-nums text-brand-800">
                {formatPriceHuf(parseIntOrNull(priceOverrideDraft) ?? 0)}
              </span>{" "}
              (kézi felülírás)
            </span>
          ) : (
            <span className="text-brand-400">
              Érvényes ár:{" "}
              <span className="font-semibold tabular-nums text-brand-700">
                {formatPriceHuf(preview.grossHuf)}
              </span>{" "}
              (számított)
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Egy ár-tétel szerkesztő sora + a hozzá számolt bruttó. */
function PriceItemRow({
  draft,
  line,
  onChange,
  onRemove,
}: {
  draft: PriceItemDraft;
  line: { grossHuf: number; quantity: number } | undefined;
  onChange: (patch: Partial<PriceItemDraft>) => void;
  onRemove: () => void;
}) {
  const isExtra = draft.kind === "extra";
  const extraSqm = isExtra && draft.unit === "sqm";
  const setPrice = (v: string) => {
    const s = sanitizeInt(v);
    if (INT_INPUT_RE.test(s)) onChange({ unitPriceHuf: s });
  };
  // m² egységnél tört mennyiség is megadható (pl. 5,2 m² tároló).
  const setQty = (v: string) => {
    if (extraSqm) {
      const s = sanitizeDecimal(v);
      if (DECIMAL_INPUT_RE.test(s)) onChange({ quantity: s });
      return;
    }
    const s = sanitizeInt(v);
    if (INT_INPUT_RE.test(s)) onChange({ quantity: s });
  };
  const setVat = (v: string) => {
    const s = sanitizeDecimal(v);
    if (DECIMAL_INPUT_RE.test(s)) onChange({ vatPct: s });
  };

  return (
    <div className="rounded-md border border-brand-200 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-brand-700">
          {draft.boundToM2
            ? boundToM2Label(draft.boundToM2)
            : isExtra
              ? "Extra"
              : priceItemKindLabel(draft.kind)}
          {draft.boundToM2 && (
            <span className="ml-1.5 font-normal text-brand-400">
              · m²-hez kötve (élő)
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-500 hover:text-red-700"
          title="Tétel törlése"
        >
          <Trash2 size={15} />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        {isExtra && (
          <div className="col-span-2">
            <label className="label !text-[11px]">Megnevezés</label>
            <input
              className="input !py-1.5"
              placeholder="pl. Mélygarázs hely"
              value={draft.label}
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </div>
        )}
        <div>
          <label className="label !text-[11px]">
            {isExtra
              ? extraSqm
                ? "Egységár (Ft/m²)"
                : "Egységár (Ft/db)"
              : "m²-ár (Ft)"}
          </label>
          <input
            type="text"
            inputMode="numeric"
            className="input !py-1.5 text-right"
            value={draft.unitPriceHuf}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
        {isExtra ? (
          <div className="flex items-end gap-1.5">
            <div className="flex-1 min-w-0">
              <label className="label !text-[11px]">
                {extraSqm ? "Mennyiség (m²)" : "Darab"}
              </label>
              <input
                type="text"
                inputMode={extraSqm ? "decimal" : "numeric"}
                className="input !py-1.5 text-right"
                value={draft.quantity}
                onChange={(e) => setQty(e.target.value)}
              />
            </div>
            <select
              className="input !py-1.5 !w-auto shrink-0"
              title="Mennyiség egysége"
              value={draft.unit}
              onChange={(e) =>
                onChange({ unit: e.target.value as "db" | "sqm" })
              }
            >
              <option value="db">db</option>
              <option value="sqm">m²</option>
            </select>
          </div>
        ) : (
          <div>
            <label className="label !text-[11px]">Mennyiség (m²)</label>
            <div className="input !py-1.5 text-right bg-brand-50 text-brand-500 tabular-nums">
              {line ? line.quantity.toLocaleString("hu-HU") : "—"}
            </div>
          </div>
        )}
        <div>
          <label className="label !text-[11px]">Ár típusa</label>
          <select
            className="input !py-1.5"
            value={draft.isGross ? "gross" : "net"}
            onChange={(e) => onChange({ isGross: e.target.value === "gross" })}
          >
            <option value="gross">Bruttó</option>
            <option value="net">Nettó</option>
          </select>
        </div>
        <div>
          <label className="label !text-[11px]">ÁFA (%)</label>
          <input
            type="text"
            inputMode="decimal"
            className="input !py-1.5 text-right"
            value={draft.vatPct}
            onChange={(e) => setVat(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
        <label
          className={`flex items-center gap-1.5 text-xs ${
            draft.isGross ? "text-brand-300" : "text-brand-700"
          }`}
          title={
            draft.isGross
              ? "Bruttó árnál nincs jelentősége (az ÁFA már benne van)"
              : "Nettó árra +ÁFA hozzáadandó-e"
          }
        >
          <input
            type="checkbox"
            disabled={draft.isGross}
            checked={draft.plusVat}
            onChange={(e) => onChange({ plusVat: e.target.checked })}
          />
          +ÁFA értendő
        </label>
        {isExtra && (
          <span className="text-xs text-brand-400">
            Opcionális extra — nem része a lakás árának
          </span>
        )}
        <span className="ml-auto text-xs text-brand-500">
          Bruttó:{" "}
          <span className="font-semibold text-brand-800 tabular-nums">
            {line ? formatPriceHuf(line.grossHuf) : "—"}
          </span>
        </span>
      </div>
    </div>
  );
}

// ─── Full per-unit editor (details + media + VR + styles) ─────────────────

function UnitFullEditor({
  unit,
  onSave,
  saving,
  saved,
  pricingDefaults,
  liveAreaSqm,
  pricingDraft,
  onPricingDraftChange,
  priceOverrideDraft,
  onPriceOverrideChange,
}: {
  unit: ProjectUnit;
  onSave: (data: Partial<NewProjectUnit>) => void;
  saving: boolean;
  saved: boolean;
  pricingDefaults: PricingDefaults | null;
  /** A gyorssorban élőben szerkesztett alapterület (m²) — az Árazás blokk
   *  base_sqm tételéhez, hogy az azonnal kövesse a gyorssor bevitelét. */
  liveAreaSqm: string;
  /** Erkély/terasz/kert m² + ár-tételek élő állapota (a szülő UnitRow-ban). */
  pricingDraft: PricingDraft;
  onPricingDraftChange: (next: PricingDraft) => void;
  /** Kézi ár-felülírás élő draftja (a szülő UnitRow QuickDraft-jában) — a
   *  gyorssorral közös, hogy a két hely ne csússzon el. */
  priceOverrideDraft: string;
  onPriceOverrideChange: (v: string) => void;
}) {
  const [draft, setDraft] = useState(() => ({
    slug: unit.slug ?? "",
    description: unit.description ?? "",
    orientation: unit.orientation ?? "",
    energyRating: unit.energyRating ?? "",
    heatingType: unit.heatingType ?? "",
    notes: unit.notes ?? "",
    storageAreaSqm: (unit.storageAreaSqm as unknown as string | null) ?? "",
    parkingIncluded: unit.parkingIncluded,
    parkingCount: unit.parkingCount == null ? "" : String(unit.parkingCount),
    storageIncluded: unit.storageIncluded,
    virtualTourUrl: unit.virtualTourUrl ?? "",
  }));

  // Media changes save immediately (same pattern as the project sections);
  // text/number fields save on the explicit button so partial typing
  // doesn't spam the API. Numeric fields are normalized via the shared
  // parseDecimal / parseIntOrNull helpers (Hungarian comma → dot, no NaN).
  function saveDetails() {
    const textOrNull = (v: string) => (v.trim() === "" ? null : v.trim());
    onSave({
      slug: draft.slug.trim() === "" ? null : draft.slug.trim(),
      description: draft.description.trim() === "" ? null : draft.description,
      orientation: textOrNull(draft.orientation),
      energyRating: textOrNull(draft.energyRating),
      heatingType: textOrNull(draft.heatingType),
      notes: textOrNull(draft.notes),
      balconyAreaSqm: parseDecimal(pricingDraft.balconyAreaSqm),
      terraceAreaSqm: parseDecimal(pricingDraft.terraceAreaSqm),
      gardenAreaSqm: parseDecimal(pricingDraft.gardenAreaSqm),
      storageAreaSqm: parseDecimal(draft.storageAreaSqm),
      parkingIncluded: draft.parkingIncluded,
      parkingCount: parseIntOrNull(draft.parkingCount),
      storageIncluded: draft.storageIncluded,
      virtualTourUrl: textOrNull(draft.virtualTourUrl),
      priceItems: pricingDraft.priceItems.map(priceItemFromDraft),
      priceOverrideHuf: parseIntOrNull(priceOverrideDraft),
    });
  }

  function set<K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  /** Guarded onChange for the (locally-owned) storage m² field. */
  function setDecimal(key: "storageAreaSqm", value: string) {
    const v = sanitizeDecimal(value);
    if (DECIMAL_INPUT_RE.test(v)) set(key, v);
  }
  /** Guarded onChange for integer fields. */
  function setInt(key: "parkingCount", value: string) {
    const v = sanitizeInt(value);
    if (INT_INPUT_RE.test(v)) set(key, v);
  }
  /** Guarded onChange for the shared (lifted) erkély/terasz/kert m² fields. */
  function setPricingDecimal(
    key: "balconyAreaSqm" | "terraceAreaSqm" | "gardenAreaSqm",
    value: string,
  ) {
    const v = sanitizeDecimal(value);
    if (DECIMAL_INPUT_RE.test(v)) onPricingDraftChange({ ...pricingDraft, [key]: v });
  }

  return (
    <div className="space-y-6">
      {/* Részletek */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="label">URL azonosító (slug)</label>
          <input
            className="input font-mono"
            placeholder="pl. 2b-lakas"
            value={draft.slug}
            onChange={(e) => set("slug", e.target.value)}
          />
        </div>
        <div>
          <label className="label">Tájolás</label>
          <input
            className="input"
            placeholder="pl. Déli"
            value={draft.orientation}
            onChange={(e) => set("orientation", e.target.value)}
          />
        </div>
        <div>
          <label className="label">Energetikai besorolás</label>
          <input
            className="input"
            placeholder="pl. AA"
            value={draft.energyRating}
            onChange={(e) => set("energyRating", e.target.value)}
          />
        </div>
        <div>
          <label className="label">Fűtés</label>
          <input
            className="input"
            placeholder="pl. Padlófűtés"
            value={draft.heatingType}
            onChange={(e) => set("heatingType", e.target.value)}
          />
        </div>
        <div>
          <label className="label">Erkély (m²)</label>
          <input
            type="text"
            inputMode="decimal"
            className="input"
            value={pricingDraft.balconyAreaSqm}
            onChange={(e) => setPricingDecimal("balconyAreaSqm", e.target.value)}
          />
        </div>
        <div>
          <label className="label">Terasz (m²)</label>
          <input
            type="text"
            inputMode="decimal"
            className="input"
            value={pricingDraft.terraceAreaSqm}
            onChange={(e) => setPricingDecimal("terraceAreaSqm", e.target.value)}
          />
        </div>
        <div>
          <label className="label">Saját kert (m²)</label>
          <input
            type="text"
            inputMode="decimal"
            className="input"
            value={pricingDraft.gardenAreaSqm}
            onChange={(e) => setPricingDecimal("gardenAreaSqm", e.target.value)}
          />
        </div>
        <div>
          <label className="label">Tároló (m²)</label>
          <input
            type="text"
            inputMode="decimal"
            className="input"
            value={draft.storageAreaSqm}
            onChange={(e) => setDecimal("storageAreaSqm", e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.parkingIncluded}
            onChange={(e) => set("parkingIncluded", e.target.checked)}
          />
          Parkolóhely jár
        </label>
        {draft.parkingIncluded && (
          <label className="flex items-center gap-2 text-sm">
            Darab:
            <input
              type="text"
              inputMode="numeric"
              className="input !w-20 !py-1.5"
              value={draft.parkingCount}
              onChange={(e) => setInt("parkingCount", e.target.value)}
            />
          </label>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.storageIncluded}
            onChange={(e) => set("storageIncluded", e.target.checked)}
          />
          Tároló jár
        </label>
      </div>

      <UnitPricingEditor
        items={pricingDraft.priceItems}
        onChange={(priceItems) =>
          onPricingDraftChange({ ...pricingDraft, priceItems })
        }
        areas={{
          areaSqm: liveAreaSqm === "" ? null : liveAreaSqm,
          balconyAreaSqm: pricingDraft.balconyAreaSqm,
          terraceAreaSqm: pricingDraft.terraceAreaSqm,
          gardenAreaSqm: pricingDraft.gardenAreaSqm,
        }}
        priceHuf={unit.priceHuf ?? null}
        priceOverrideDraft={priceOverrideDraft}
        onPriceOverrideChange={onPriceOverrideChange}
        defaults={pricingDefaults}
      />

      <div>
        <label className="label">Leírás (a lakás aloldalán jelenik meg)</label>
        <textarea
          rows={5}
          className="input"
          value={draft.description}
          onChange={(e) => set("description", e.target.value)}
        />
      </div>

      <div>
        <label className="label">Virtuális séta (VR)</label>
        <input
          className="input font-mono text-xs"
          placeholder="https://my.matterport.com/show/?m=... vagy Pannellum / Kuula link"
          value={draft.virtualTourUrl}
          onChange={(e) => set("virtualTourUrl", e.target.value)}
        />
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <PanoramaUpload
            unitId={unit.id}
            onUploaded={(url) => {
              set("virtualTourUrl", url);
              onSave({ virtualTourUrl: url });
            }}
          />
          {draft.virtualTourUrl && (
            <a
              href={draft.virtualTourUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-accent underline hover:text-accent-700"
            >
              Séta megnyitása / tesztelése ↗
            </a>
          )}
        </div>
        <p className="mt-1.5 text-xs text-brand-500">
          Két lehetőség: (1) illesszen be egy Matterport / Kuula / Pannellum
          beágyazható linket, VAGY (2) töltsön fel egy saját 360°-os
          panorámafotót (equirectangular, 2:1 oldalarányú JPG — minden 360
          kamera ilyet készít), és a linket automatikusan beállítjuk.
        </p>
      </div>

      <div>
        <label className="label">Belső megjegyzés (nem publikus)</label>
        <input
          className="input"
          value={draft.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={saveDetails}
          disabled={saving}
          className="btn-primary"
        >
          {saving ? "Mentés…" : "Részletek mentése"}
        </button>
        {saved && !saving && (
          <span className="inline-flex items-center gap-1 text-sm text-green-600">
            <Check size={15} /> Mentve
          </span>
        )}
      </div>

      <hr className="border-brand-200" />

      <GallerySection
        flat
        title="Lakás galéria (képek + videók)"
        prefix={`units/${unit.id}/gallery`}
        gallery={unit.gallery}
        onChange={(gallery) => onSave({ gallery })}
      />

      <hr className="border-brand-200" />

      <FloorplansSection
        flat
        showDisplayMode
        title="Lakás alaprajzok"
        prefix={`units/${unit.id}/floorplans`}
        floorplans={unit.floorplans}
        onChange={(floorplans) => onSave({ floorplans })}
      />

      <hr className="border-brand-200" />

      <StylePreviewsEditor
        unitId={unit.id}
        previews={unit.stylePreviews}
        onChange={(stylePreviews) => onSave({ stylePreviews })}
      />
    </div>
  );
}

// ─── 360° panorama upload → auto Pannellum viewer URL ─────────────────────

function PanoramaUpload({
  unitId,
  onUploaded,
}: {
  unitId: string;
  onUploaded: (viewerUrl: string) => void;
}) {
  const { uploading, uploadError, uploadSuccess, run } = useUpload();
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFile(file: File) {
    run(async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", `units/${unitId}/panorama`);
      const res = await api<{ key: string }>("/api/admin/upload", {
        method: "POST",
        formData: fd,
      });
      // The hosted Pannellum viewer takes the panorama URL in the
      // fragment — it must be ABSOLUTE because the iframe runs on
      // cdn.pannellum.org (the /api/files proxy sends CORS headers).
      const absolute = `${window.location.origin}${fileUrl(res.key)}`;
      const viewerUrl = `https://cdn.pannellum.org/2.5/pannellum.htm#panorama=${encodeURIComponent(
        absolute,
      )}&autoLoad=true&autoRotate=-2`;
      onUploaded(viewerUrl);
    });
  }

  return (
    <div className="flex flex-col gap-1">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="btn-secondary !py-2 !px-4 text-xs"
      >
        <Upload size={14} />
        {uploading ? "Feltöltés..." : "360° panoráma feltöltése"}
      </button>
      {uploadError && (
        <p className="text-xs text-red-600">{uploadError}</p>
      )}
      {uploadSuccess && (
        <p className="text-xs text-green-600">Feltöltve ✓</p>
      )}
    </div>
  );
}

// ─── Style previews (Modern / Klasszikus / ... render variants) ───────────

function StylePreviewsEditor({
  unitId,
  previews,
  onChange,
}: {
  unitId: string;
  previews: StylePreview[];
  onChange: (p: StylePreview[]) => void;
}) {
  const { uploading, uploadError, uploadSuccess, run } = useUpload();
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFile(file: File) {
    run(async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", `units/${unitId}/styles`);
      const res = await api<{ key: string }>("/api/admin/upload", {
        method: "POST",
        formData: fd,
      });
      onChange([
        ...previews,
        {
          label: `Stílus ${previews.length + 1}`,
          imageKey: res.key,
          description: "",
        },
      ]);
    });
  }

  function patch(i: number, p: Partial<StylePreview>) {
    onChange(previews.map((s, idx) => (idx === i ? { ...s, ...p } : s)));
  }

  function remove(i: number) {
    onChange(previews.filter((_, idx) => idx !== i));
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-semibold text-sm">Belső stílus választó</h2>
          <p className="text-xs text-brand-500 mt-0.5">
            Renderek stílusonként (Modern, Klasszikus, Skandináv...) — a
            látogató a lakás oldalán válthat köztük.
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-secondary"
        >
          <Upload size={16} />
          {uploading ? "Feltöltés..." : "Stílus hozzáadása"}
        </button>
      </div>
      {uploadError && (
        <p className="mt-2 text-sm text-red-600">{uploadError}</p>
      )}
      {uploadSuccess && (
        <p className="mt-2 text-sm text-green-600">Feltöltve ✓</p>
      )}

      {previews.length === 0 ? (
        <p className="mt-3 text-sm text-brand-500">
          Még nincs stílusvariáns feltöltve.
        </p>
      ) : (
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {previews.map((s, i) => (
            <div
              key={s.imageKey + i}
              className="border border-brand-200 rounded-md overflow-hidden bg-white"
            >
              <img
                src={fileUrl(s.imageKey)}
                alt={s.label}
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="p-2.5 space-y-2">
                <input
                  className="input !py-1.5 text-sm"
                  placeholder="Stílus neve"
                  value={s.label}
                  onChange={(e) => patch(i, { label: e.target.value })}
                />
                <input
                  className="input !py-1.5 text-xs"
                  placeholder="Rövid leírás (opcionális)"
                  value={s.description ?? ""}
                  onChange={(e) => patch(i, { description: e.target.value })}
                />

                {/* Per-style VR séta URL (clearable) */}
                <div>
                  <label className="label !text-[11px] !mb-1">
                    VR séta URL (opcionális)
                  </label>
                  <div className="flex items-center gap-1.5">
                    <input
                      className="input !py-1.5 font-mono text-[11px]"
                      placeholder="https://my.matterport.com/show/?m=… / Kuula / Pannellum"
                      value={s.vrTourUrl ?? ""}
                      onChange={(e) =>
                        patch(i, { vrTourUrl: e.target.value })
                      }
                    />
                    {s.vrTourUrl && (
                      <button
                        type="button"
                        onClick={() => patch(i, { vrTourUrl: null })}
                        className="shrink-0 w-7 h-7 grid place-items-center rounded text-brand-500 hover:text-red-600 hover:bg-red-50"
                        title="VR URL törlése"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Per-style gallery (multi-upload + preview + delete) */}
                <div className="pt-1">
                  <GallerySection
                    flat
                    title="Stílus galéria"
                    prefix={`units/${unitId}/styles/${i}/gallery`}
                    gallery={s.galleryImages ?? []}
                    onChange={(galleryImages) =>
                      patch(i, { galleryImages })
                    }
                  />
                </div>

                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                >
                  <Trash2 size={12} /> Eltávolítás
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Neighborhood (Környék és elhelyezkedés) ─────────────────────────────────

function parseCoord(s: string | null | undefined): number | null {
  if (s == null) return null;
  const n = Number.parseFloat(String(s).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/** A place candidate returned by the /api/admin/geocode/search proxy. */
type PlaceHit = {
  lat: number;
  lng: number;
  name: string;
  displayName: string;
};

/** Great-circle distance between two coordinates, in meters (haversine). */
function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Human-readable distance for the "Közeli helyek" list: below ~1 km rounded
 * to 50 m ("kb. 300 m"), above that one decimal in km ("kb. 1,2 km").
 */
function formatPoiDistance(meters: number): string {
  if (meters < 950) {
    const rounded = Math.max(50, Math.round(meters / 50) * 50);
    return `kb. ${rounded} m`;
  }
  const km = (meters / 1000).toFixed(1).replace(".", ",");
  return `kb. ${km} km`;
}

function apiErrorMessage(err: unknown, fallback: string): string {
  if (
    err instanceof ApiError &&
    err.body &&
    typeof err.body === "object" &&
    "message" in err.body
  ) {
    return String((err.body as { message: unknown }).message);
  }
  if (err instanceof ApiError) return err.message;
  return fallback;
}

function NeighborhoodSection({
  address,
  lat,
  lng,
  pois,
  description,
  onSave,
}: {
  address: string;
  lat: string | null;
  lng: string | null;
  pois: NeighborhoodPoi[];
  description: string;
  onSave: (partial: Partial<NewProject>) => Promise<unknown>;
}) {
  const qc = useQueryClient();
  const categoriesQ = useQuery({
    queryKey: ["admin", "poi-categories"],
    queryFn: () => api<PoiCategory[]>("/api/admin/poi-categories"),
  });
  const categories = categoriesQ.data ?? [];

  // Local editable state — persisted explicitly via "Szekció mentése".
  const [addr, setAddr] = useState(address);
  const [latStr, setLatStr] = useState(lat ?? "");
  const [lngStr, setLngStr] = useState(lng ?? "");
  const [rows, setRows] = useState<NeighborhoodPoi[]>(pois);
  const [desc, setDesc] = useState(description);
  // Coordinate inputs are hidden by default behind "Haladó beállítások" — the
  // address-first flow (type address → position map → drag pin) is primary.
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [geoBusy, setGeoBusy] = useState(false);
  const [geoMsg, setGeoMsg] = useState<{
    kind: "ok" | "err";
    text: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Place search for "Közeli helyek" — settlement + place name → candidates.
  const [placeQuery, setPlaceQuery] = useState("");
  const [placeBusy, setPlaceBusy] = useState(false);
  const [placeResults, setPlaceResults] = useState<PlaceHit[] | null>(null);
  const [placeMsg, setPlaceMsg] = useState<string | null>(null);

  // Re-sync when the project reloads (after save / navigation).
  useEffect(() => setAddr(address), [address]);
  useEffect(() => setLatStr(lat ?? ""), [lat]);
  useEffect(() => setLngStr(lng ?? ""), [lng]);
  useEffect(() => setRows(pois), [pois]);
  useEffect(() => setDesc(description), [description]);

  const latNum = parseCoord(latStr);
  const lngNum = parseCoord(lngStr);

  async function geocode() {
    if (!addr.trim()) {
      setGeoMsg({
        kind: "err",
        text: "Előbb adja meg az ingatlan címét.",
      });
      return;
    }
    setGeoBusy(true);
    setGeoMsg(null);
    try {
      const geo = await api<{ lat: number; lng: number; displayName: string }>(
        "/api/admin/geocode",
        { method: "POST", body: { address: addr } },
      );
      setLatStr(geo.lat.toFixed(7));
      setLngStr(geo.lng.toFixed(7));
      setGeoMsg({ kind: "ok", text: `Megtalálva: ${geo.displayName}` });
    } catch (err) {
      setGeoMsg({
        kind: "err",
        text: apiErrorMessage(
          err,
          "A geokódolás sikertelen — adja meg kézzel a koordinátákat.",
        ),
      });
    } finally {
      setGeoBusy(false);
    }
  }

  function setPin(la: number, ln: number) {
    setLatStr(la.toFixed(7));
    setLngStr(ln.toFixed(7));
  }

  function addPoi() {
    const cat = categories[0];
    setRows((r) => [
      ...r,
      {
        name: "",
        category: cat?.label ?? "",
        distance: "",
        icon: cat?.icon ?? "",
      },
    ]);
  }
  function updatePoi(i: number, patch: Partial<NeighborhoodPoi>) {
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }
  function removePoi(i: number) {
    setRows((r) => r.filter((_, idx) => idx !== i));
  }
  function pickCategory(i: number, label: string) {
    const cat = categories.find((c) => c.label === label);
    updatePoi(i, { category: label, icon: cat?.icon ?? rows[i]?.icon ?? "" });
  }

  async function searchPlace() {
    const q = placeQuery.trim();
    if (!q || placeBusy) return;
    setPlaceBusy(true);
    setPlaceMsg(null);
    setPlaceResults(null);
    try {
      const hits = await api<PlaceHit[]>("/api/admin/geocode/search", {
        method: "POST",
        body: { query: q },
      });
      if (hits.length === 0) {
        setPlaceMsg(
          "Nincs találat — próbálja meg a település nevével együtt (pl. „Sopron Széchenyi iskola”).",
        );
      } else {
        setPlaceResults(hits);
      }
    } catch (err) {
      setPlaceMsg(
        apiErrorMessage(err, "A keresés sikertelen — próbálja meg később."),
      );
    } finally {
      setPlaceBusy(false);
    }
  }

  function pickPlace(hit: PlaceHit) {
    // Distance from the project's coordinates (haversine) — editable after.
    const distance =
      latNum != null && lngNum != null
        ? formatPoiDistance(
            haversineMeters(latNum, lngNum, hit.lat, hit.lng),
          )
        : "";
    setRows((r) => [
      ...r,
      { name: hit.name, category: "", distance, icon: "" },
    ]);
    setPlaceResults(null);
    setPlaceQuery("");
    setPlaceMsg(null);
  }

  async function saveAll() {
    const cleaned = rows
      .map((r) => ({
        name: r.name.trim(),
        category: r.category.trim(),
        distance: r.distance.trim(),
        icon: r.icon?.trim() ? r.icon.trim() : undefined,
      }))
      .filter((r) => r.name || r.category || r.distance);
    if (cleaned.some((r) => !r.name || !r.category || !r.distance)) {
      setSaveMsg("Minden közeli helynél kötelező a név, a típus és a távolság.");
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      await onSave({
        locationAddress: addr.trim() === "" ? null : addr.trim(),
        locationLat: latStr.trim() === "" ? null : latStr.trim(),
        locationLng: lngStr.trim() === "" ? null : lngStr.trim(),
        neighborhoodPois: cleaned,
        neighborhoodDescription: desc.trim() === "" ? null : desc,
      });
      setRows(cleaned);
      setSaveMsg("Mentve ✓");
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err) {
      setSaveMsg(
        err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : "Mentési hiba",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card p-6 space-y-6">
      <div>
        <h2 className="font-semibold text-lg flex items-center gap-2">
          <MapPin size={18} className="text-accent" /> Környék és elhelyezkedés
        </h2>
        <p className="text-sm text-brand-500 mt-1">
          Az ingatlan címe, egy térkép és a közeli helyek listája. Az üres
          részek nem jelennek meg a publikus oldalon.
        </p>
      </div>

      {/* 1 — Address → map */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm">Az ingatlan címe</h3>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label className="label">Cím</label>
            <input
              className="input"
              value={addr}
              onChange={(e) => setAddr(e.target.value)}
              placeholder="pl. 1117 Budapest, Példa utca 12."
            />
          </div>
          <button
            type="button"
            onClick={geocode}
            disabled={geoBusy}
            className="btn-secondary shrink-0"
          >
            <LocateFixed size={16} />
            {geoBusy ? "Keresés..." : "Keresés a térképen"}
          </button>
        </div>
        {geoMsg && (
          <p
            className={`text-sm ${geoMsg.kind === "ok" ? "text-green-600" : "text-red-600"}`}
          >
            {geoMsg.text}
          </p>
        )}
        <p className="text-xs text-brand-500">
          Írja be a címet, majd kattintson a „Keresés a térképen” gombra — a
          térkép automatikusan a helyszínre ugrik. A pontos helyet a jelölő
          húzásával vagy a térképre kattintva finomíthatja.
        </p>
        {latNum != null && lngNum != null ? (
          <Suspense
            fallback={
              <div className="h-72 bg-brand-100 grid place-items-center text-sm text-brand-500">
                Térkép betöltése…
              </div>
            }
          >
            <NeighborhoodMapPicker
              lat={latNum}
              lng={lngNum}
              onChange={setPin}
              className="h-72 w-full border border-brand-200"
            />
          </Suspense>
        ) : (
          <div className="h-40 bg-brand-50 border border-dashed border-brand-200 grid place-items-center text-sm text-brand-400 text-center px-4">
            Adja meg a címet, majd kattintson a „Keresés a térképen” gombra a
            térkép megjelenítéséhez.
          </div>
        )}
      </div>

      {/* 2 — Nearby places */}
      <div className="space-y-3 border-t border-brand-100 pt-6">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-sm">Közeli helyek</h3>
          <button
            type="button"
            onClick={addPoi}
            disabled={categories.length === 0}
            className="btn-secondary text-xs"
          >
            <Plus size={14} /> Közeli hely hozzáadása
          </button>
        </div>

        {/* Place search: settlement + place name → pre-filled row */}
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <label className="label">Hely keresése</label>
              <input
                className="input"
                value={placeQuery}
                onChange={(e) => setPlaceQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void searchPlace();
                  }
                }}
                placeholder="pl. Sopron Széchenyi iskola"
              />
            </div>
            <button
              type="button"
              onClick={() => void searchPlace()}
              disabled={placeBusy || !placeQuery.trim()}
              className="btn-secondary shrink-0"
            >
              <Search size={16} />
              {placeBusy ? "Keresés..." : "Keresés"}
            </button>
          </div>
          <p className="text-xs text-brand-500">
            Írja be a települést és a hely nevét, válasszon a találatok közül —
            a név és a távolság automatikusan kitöltődik, a típust utána
            választhatja ki.
          </p>
          {latNum == null || lngNum == null ? (
            <p className="text-xs text-amber-600">
              Előbb adja meg a címet vagy pozicionálja a térképet — enélkül a
              távolságot nem tudjuk automatikusan kiszámolni.
            </p>
          ) : null}
          {placeMsg && <p className="text-sm text-red-600">{placeMsg}</p>}
          {placeResults && placeResults.length > 0 && (
            <ul className="border border-brand-200 divide-y divide-brand-100 rounded bg-white shadow-sm">
              {placeResults.map((hit, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => pickPlace(hit)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50"
                  >
                    <span className="font-medium">{hit.name}</span>
                    <span className="block text-xs text-brand-500">
                      {hit.displayName}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {rows.length === 0 && (
          <p className="text-sm text-brand-400">
            Még nincs közeli hely. Adjon hozzá iskolát, boltot, megállót…
          </p>
        )}
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-[1fr_9rem_8rem_4rem_auto] gap-2 items-center bg-brand-50 border border-brand-100 p-2 rounded"
            >
              <input
                className="input"
                placeholder="Név (pl. Kelenföldi iskola)"
                value={row.name}
                onChange={(e) => updatePoi(i, { name: e.target.value })}
              />
              <select
                className="input"
                value={row.category}
                onChange={(e) => pickCategory(i, e.target.value)}
              >
                {!row.category && (
                  <option value="" disabled>
                    Válasszon típust…
                  </option>
                )}
                {row.category &&
                  !categories.some((c) => c.label === row.category) && (
                    <option value={row.category}>{row.category}</option>
                  )}
                {categories.map((c) => (
                  <option key={c.id} value={c.label}>
                    {c.icon} {c.label}
                  </option>
                ))}
              </select>
              <input
                className="input"
                placeholder="Távolság (pl. 300 m)"
                value={row.distance}
                onChange={(e) => updatePoi(i, { distance: e.target.value })}
              />
              <input
                className="input text-center"
                placeholder="🏫"
                value={row.icon ?? ""}
                onChange={(e) => updatePoi(i, { icon: e.target.value })}
                maxLength={4}
                aria-label="Ikon / emoji (opcionális)"
              />
              <button
                type="button"
                onClick={() => removePoi(i)}
                className="w-9 h-9 grid place-items-center rounded text-red-500 hover:bg-red-50 hover:text-red-700 justify-self-start sm:justify-self-center"
                aria-label="Közeli hely törlése"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <CategoryManager
          categories={categories}
          onChanged={() =>
            qc.invalidateQueries({ queryKey: ["admin", "poi-categories"] })
          }
        />
      </div>

      {/* 3 — Description */}
      <div className="space-y-2 border-t border-brand-100 pt-6">
        <h3 className="font-semibold text-sm">A környék leírása</h3>
        <textarea
          className="input"
          rows={5}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Pár mondat a környékről: hangulat, infrastruktúra, közlekedés, zöldterületek…"
        />
      </div>

      {/* 4 — Advanced: raw coordinates (hidden by default) */}
      <div className="border-t border-brand-100 pt-4">
        <button
          type="button"
          onClick={() => setAdvancedOpen((o) => !o)}
          className="text-xs font-semibold text-brand-600 inline-flex items-center gap-1"
        >
          <ChevronUp size={14} className={advancedOpen ? "" : "rotate-180"} />
          Haladó beállítások
        </button>
        {advancedOpen && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-brand-500">
              A térkép koordinátái. Ezeket általában nem kell kézzel megadni — a
              „Keresés a térképen” gomb és a jelölő húzása automatikusan
              beállítja őket.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="label">Szélesség (lat)</label>
                <input
                  className="input font-mono w-40"
                  value={latStr}
                  onChange={(e) => setLatStr(e.target.value)}
                  placeholder="47.4979"
                  inputMode="decimal"
                />
              </div>
              <div>
                <label className="label">Hosszúság (lng)</label>
                <input
                  className="input font-mono w-40"
                  value={lngStr}
                  onChange={(e) => setLngStr(e.target.value)}
                  placeholder="19.0402"
                  inputMode="decimal"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 border-t border-brand-100 pt-4">
        <button
          type="button"
          onClick={saveAll}
          disabled={saving}
          className="btn-primary"
        >
          {saving ? "Mentés..." : "Szekció mentése"}
        </button>
        {saveMsg && <span className="text-sm text-brand-600">{saveMsg}</span>}
      </div>
    </section>
  );
}

function CategoryManager({
  categories,
  onChanged,
}: {
  categories: PoiCategory[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [icon, setIcon] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    if (!label.trim() || !icon.trim()) {
      setErr("Adjon meg megnevezést és ikont.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api("/api/admin/poi-categories", {
        method: "POST",
        body: {
          label: label.trim(),
          icon: icon.trim(),
          sortOrder: categories.length,
        },
      });
      setLabel("");
      setIcon("");
      onChanged();
    } catch (e) {
      setErr(apiErrorMessage(e, "Hiba a kategória hozzáadásakor."));
    } finally {
      setBusy(false);
    }
  }

  async function del(id: string) {
    try {
      await api(`/api/admin/poi-categories/${id}`, { method: "DELETE" });
      onChanged();
    } catch (e) {
      setErr(apiErrorMessage(e, "Hiba a kategória törlésekor."));
    }
  }

  return (
    <div className="mt-3 bg-white border border-brand-100 rounded p-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs font-semibold text-brand-600 inline-flex items-center gap-1"
      >
        <ChevronUp size={14} className={open ? "" : "rotate-180"} />
        Kategóriák kezelése ({categories.length})
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            {categories.length === 0 && (
              <span className="text-sm text-brand-400">Nincs kategória.</span>
            )}
            {categories.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1.5 bg-brand-50 border border-brand-200 rounded-full pl-2.5 pr-1 py-1 text-sm"
              >
                <span aria-hidden="true">{c.icon}</span>
                <span className="text-brand-700">{c.label}</span>
                <button
                  type="button"
                  onClick={() => del(c.id)}
                  className="w-5 h-5 grid place-items-center rounded-full text-brand-400 hover:bg-red-100 hover:text-red-600"
                  aria-label={`${c.label} törlése`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="label">Új kategória</label>
              <input
                className="input w-44"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="pl. Sport"
              />
            </div>
            <div>
              <label className="label">Ikon / emoji</label>
              <input
                className="input w-20 text-center"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="🏋️"
                maxLength={4}
              />
            </div>
            <button
              type="button"
              onClick={add}
              disabled={busy}
              className="btn-secondary"
            >
              <Plus size={14} /> {busy ? "..." : "Hozzáadás"}
            </button>
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Műszaki tartalom (PDF dokumentum + strukturált ikon-szöveg adatsorok) ──

/** Max upload size for the technical PDF, in megabytes (client-side guard). */
const MAX_PDF_MB = 25;

function TechnicalContentSection({
  pdf,
  specs,
  onSavePdf,
  onSaveSpecs,
}: {
  pdf: TechnicalPdf | null;
  specs: TechnicalSpec[];
  /** Persists the PDF field (PATCH); resolves on success, rejects on failure. */
  onSavePdf: (pdf: TechnicalPdf | null) => Promise<unknown>;
  /** Persists the specs array (PATCH); resolves on success, rejects on failure. */
  onSaveSpecs: (specs: TechnicalSpec[]) => Promise<unknown>;
}) {
  return (
    <section className="card p-6 space-y-6">
      <div>
        <h2 className="font-semibold text-lg flex items-center gap-2">
          <FileText size={18} className="text-accent" /> Műszaki tartalom
        </h2>
        <p className="text-sm text-brand-500 mt-1">
          Feltölthető egy PDF dokumentum (műszaki leírás, alaprajz) és tetszőleges
          számú ikon+szöveg adatsor. Ha mindkettő üres, a szekció nem jelenik meg
          a publikus oldalon.
        </p>
      </div>
      <TechnicalPdfSubsection pdf={pdf} onSave={onSavePdf} />
      <TechnicalSpecsSubsection specs={specs} onSave={onSaveSpecs} />
    </section>
  );
}

function TechnicalPdfSubsection({
  pdf,
  onSave,
}: {
  pdf: TechnicalPdf | null;
  onSave: (pdf: TechnicalPdf | null) => Promise<unknown>;
}) {
  const { uploading, uploadError, uploadSuccess, run } = useUpload();
  // Local validation message kept separate from the network uploadError so a
  // wrong file type / oversize never silently no-ops (knowledge: néma hiba).
  const [localErr, setLocalErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleUpload(file: File) {
    setLocalErr(null);
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    if (!isPdf) {
      setLocalErr("Csak PDF dokumentum tölthető fel.");
      return;
    }
    if (file.size > MAX_PDF_MB * 1024 * 1024) {
      setLocalErr(`A PDF túl nagy (max. ${MAX_PDF_MB} MB).`);
      return;
    }
    run(async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "projects/technical");
      const res = await api<{
        key: string;
        mimeType: string;
        size: number;
        originalName: string;
      }>("/api/admin/upload", { method: "POST", formData: fd });
      // Await the PATCH so success/error reflects whether the key actually
      // persisted to the project record — not just the R2 upload.
      await onSave({
        key: res.key,
        name: res.originalName || file.name,
        size: res.size,
        mimeType: res.mimeType,
      });
    });
  }

  const sizeLabel = pdf?.size
    ? `${(pdf.size / (1024 * 1024)).toLocaleString("hu-HU", {
        maximumFractionDigits: 1,
      })} MB`
    : "";

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-sm">PDF dokumentum (max. 1 db)</h3>

      {pdf ? (
        <div className="flex items-center gap-3 rounded-md border border-brand-200 p-3">
          <FileText size={20} className="text-accent shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <a
              href={fileUrl(pdf.key)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-brand-700 hover:text-accent block truncate"
            >
              {pdf.name || "Műszaki dokumentum.pdf"}
            </a>
            <div className="text-xs text-brand-400">
              PDF{sizeLabel ? ` · ${sizeLabel}` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="btn-secondary !py-1.5 !px-3 text-xs"
          >
            <Upload size={14} />
            {uploading ? "Feltöltés..." : "Csere"}
          </button>
          <button
            type="button"
            onClick={() => run(async () => void (await onSave(null)))}
            disabled={uploading}
            className="text-red-500 hover:text-red-700 disabled:opacity-40"
            aria-label="PDF törlése"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ) : (
        <p className="text-sm text-brand-500">Nincs feltöltött PDF.</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleUpload(f);
          e.target.value = "";
        }}
      />

      {!pdf && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-secondary"
        >
          <Upload size={16} />
          {uploading ? "Feltöltés..." : "PDF feltöltése"}
        </button>
      )}

      <p className="text-xs text-brand-400">
        Maximális méret: {MAX_PDF_MB} MB · csak PDF formátum.
      </p>
      {(localErr || uploadError) && (
        <p className="text-sm text-red-600">{localErr || uploadError}</p>
      )}
      {uploadSuccess && <p className="text-sm text-green-600">Mentve ✓</p>}
    </div>
  );
}

function TechnicalSpecsSubsection({
  specs,
  onSave,
}: {
  specs: TechnicalSpec[];
  onSave: (specs: TechnicalSpec[]) => Promise<unknown>;
}) {
  // Local editable draft — persisted explicitly via "Műszaki adatok mentése"
  // (mirrors the Neighborhood / Map sections; avoids a PATCH per keystroke).
  const [rows, setRows] = useState<TechnicalSpec[]>(specs);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Re-sync when the project reloads (after save / navigation).
  useEffect(() => setRows(specs), [specs]);

  function add() {
    setRows((r) => [...r, { icon: "", text: "", order: r.length }]);
  }
  function updateRow(i: number, patch: Partial<TechnicalSpec>) {
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }
  function remove(i: number) {
    setRows((r) => r.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const target = i + dir;
    if (target < 0 || target >= rows.length) return;
    setRows((r) => {
      const next = [...r];
      [next[i], next[target]] = [next[target], next[i]];
      return next;
    });
  }

  async function saveAll() {
    const cleaned = rows
      .map((r, i) => ({
        icon: r.icon.trim(),
        text: r.text.trim(),
        order: i,
      }))
      .filter((r) => r.icon || r.text);
    if (cleaned.some((r) => !r.text)) {
      setSaveMsg("Minden adatsornál kötelező a szöveg.");
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      await onSave(cleaned);
      setRows(cleaned);
      setSaveMsg("Mentve ✓");
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err) {
      setSaveMsg(
        err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : "Mentési hiba",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3 border-t border-brand-100 pt-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-sm">Strukturált műszaki adatok</h3>
          <p className="text-xs text-brand-500 mt-0.5">
            Ikon (emoji vagy rövid szöveg) + leírás párok, pl. „🔥 Padlófűtés".
            A nyilakkal átrendezhetők.
          </p>
        </div>
        <button
          type="button"
          onClick={add}
          className="btn-secondary text-xs shrink-0"
        >
          <Plus size={14} /> Adatsor hozzáadása
        </button>
      </div>

      {rows.length === 0 && (
        <p className="text-sm text-brand-400">
          Még nincs adatsor. Adjon hozzá pl. fűtéstípust, nyílászárót,
          energetikai besorolást…
        </p>
      )}

      <div className="space-y-2">
        {rows.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-[3.5rem_1fr_auto] gap-2 items-center bg-brand-50 border border-brand-100 p-2 rounded"
          >
            <input
              className="input text-center"
              placeholder="🔥"
              value={row.icon}
              onChange={(e) => updateRow(i, { icon: e.target.value })}
              maxLength={40}
              aria-label="Ikon / emoji (opcionális)"
            />
            <input
              className="input"
              placeholder="Pl. Padlófűtés minden helyiségben"
              value={row.text}
              onChange={(e) => updateRow(i, { text: e.target.value })}
            />
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="w-7 h-7 grid place-items-center rounded text-brand-500 hover:bg-brand-100 disabled:opacity-30"
                aria-label="Előrébb"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === rows.length - 1}
                className="w-7 h-7 grid place-items-center rounded text-brand-500 hover:bg-brand-100 disabled:opacity-30"
                aria-label="Hátrébb"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                className="w-7 h-7 grid place-items-center rounded text-red-500 hover:bg-red-50 hover:text-red-700"
                aria-label="Adatsor törlése"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={saveAll}
          disabled={saving}
          className="btn-primary"
        >
          {saving ? "Mentés..." : "Műszaki adatok mentése"}
        </button>
        {saveMsg && <span className="text-sm text-brand-600">{saveMsg}</span>}
      </div>
    </div>
  );
}
